"use strict";

import {
  Badge,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputBase,
  InputLabel,
  MenuItem,
  Rating,
  Select,
  Skeleton,
  Switch,
} from "@mui/material";
import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useContext,
  useMemo,
} from "react";
import classes from "../../Assets/Styles/VoiceQAScreen.module.css";
import NonverbalLineNotice from "../../features/nonverbal/NonverbalLineNotice.js";
import {
  formatInputs,
  parseTimestampAsTotalSeconds,
} from "../../utils/generalFunctions";
import {
  ensureProjectFinalLanguageTranslations,
  findLatestDialogueIndexInTimeRange,
  findTranslationByLanguageCode,
  getDialoguePageEndTime,
  getDefaultVoiceIdForEmotion,
  getGeneratedAudioOptionValue,
  getLineCharacter,
  getRecommendedSpeedOption as getProjectWorkRecommendedSpeedOption,
  getSelectedGeneratedAudioValue,
  getSpeakerDisplayName,
  getTranslationDurationMessage,
  isBulkSpeedSelectable,
  isDurationFlagged,
  isTranslatedAudioOverlapping,
  shouldExtendPreviewTranslatedAudio,
} from "../../features/projectWork";
import BulkSpeedBar from "../../features/bulkSpeed/BulkSpeedBar";
import {
  pendingBulkSpeedLineIds,
  summarizeBulkSpeedRun,
  useBulkSpeedRun,
} from "../../features/bulkSpeed/useBulkSpeedRun";

import Loader from "../../Components/Loader";
import axios from "axios";
import { Store } from "../../Store";
import {
  CREATE_NEW_AUDIO,
  GENERATE_SOUND_EFFECT,
  UPDATE_ONE_SPEECH,
  GET_CHARACTERS_FOR_PROJECT,
  GET_SPEECH_FOR_PROJECT_BY_PAGE,
  CREATE_MULTIPLE_AUDIO,
  GET_SPEECH_POSITION_AROUND_TIME,
  SET_DIALOGUE_APPROVAL_STATUS,
  GET_ALL_QUALITYASSURANCE_VERSIONS,
  MERGING_FULL_API,
  ADJUST_AUDIO_SPEED,
  ADJUST_AUDIO_SPEED_AUTO,
  SEARCH_SPEECH_FOR_PROJECT,
} from "../../ApiEndpoints";
import { ToastContainer, toast } from "react-toastify";
import { useMediaQuery } from "@mui/material";
import PerformanceVideoPlayer from "../../Components/Players/PerformanceVideoPlayer";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { useParams } from "react-router-dom";

import { secondaryColor } from "../../utils/constants";
import TaskButton from "../../Components/Buttons/TaskButton";

import {
  DEFAULT_VOICE_ENGINE,
  LEGACY_AUDIO_ENGINE_LABEL,
  SOUND_EFFECT_VOICE_ENGINE,
  VOICE_ENGINE_OPTIONS,
  getSupportedVoiceEngine,
  isLegacyVoiceEngine,
  isSupportedVoiceEngine,
} from "../../Data/voiceEngines";

import ClipLoader from "react-spinners/ClipLoader";
import ClipAudioPlayer from "../../Components/Players/ClipAudioPlayer";
import NativeClipAudioPlayer from "../../Components/Players/NativeClipAudioPlayer";
import styled from "styled-components";
import { styled as muiStyled } from "@mui/material/styles";
import InputMask from "react-input-mask";
import debounce from "lodash.debounce";

import { useExperimentalFeature } from "../../utils/featureAccess";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import VolumeSelector from "../../Components/AudioTools/VolumeSelector";

import {
  DEFAULT_DIALOGUE_SEARCH_FILTERS,
  hasAnyDialogueSearchFilter,
} from "../../Components/DialogueSearchFilter/DialogueSearchFilter";
import DialogueFilterBar, {
  DIALOGUE_ATTENTION_OPTIONS,
} from "../../Components/DialogueSearchFilter/DialogueFilterBar";
import { injectToken } from "../../utils/injectToken";
import {
  hydrateDialoguePlaybackUrls,
  readAudioClientResponse,
} from "../../utils/audioMedia";
import ExportLayoutSelect from "../../features/exportLayout/ExportLayoutSelect";
import ExportOptions from "../../features/exportLayout/ExportOptions";
import ExportVideoSourceSelect from "../../features/exportLayout/ExportVideoSourceSelect";
import {
  DEFAULT_OUTPUT_AUDIO_LAYOUT,
  DEFAULT_VIDEO_SOURCE,
} from "../../features/exportLayout/audioLayout";
import { useDialoguePlaybackRefresh } from "../../hooks/useDialoguePlaybackRefresh";
import { useProjectMediaPlayback } from "../../hooks/useProjectMediaPlayback";

// Compact iOS-style switch (pill track, inset thumb, no ripple) sized to fit
// the 24px dialogue row header line. Uses MUI's styled so these rules share
// MUI's style engine and reliably override the Switch defaults.
const RowFlagSwitch = muiStyled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(() => ({
  width: 32,
  height: 18,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "200ms",
    "&.Mui-checked": {
      transform: "translateX(14px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "var(--color-link-text)",
        opacity: 1,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      outline: "2px solid var(--color-link-text)",
      outlineOffset: 2,
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: 0.5,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 14,
    height: 14,
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.25)",
  },
  "& .MuiSwitch-track": {
    borderRadius: 9,
    backgroundColor: "#c4c7cc",
    opacity: 1,
    transition: "background-color 200ms",
  },
}));

const BootstrapInput = styled(InputBase)(({ theme }) => ({
  "label + &": {},
  "& .MuiInputBase-input": {
    width: "0px",
    borderRadius: "var(--radius-md)",
    position: "relative",
    backgroundColor: "none",
    border: "none",
    fontSize: "var(--font-size-normal)",
    padding: "6px 20px 6px 12px",
    color: "inherit",
  },
}));

const BootstrapInputForAvalialbe = styled(InputBase)(({ theme }) => ({
  "& .MuiInputBase-input": {
    color: "inherit",
    width: "100%",
    position: "relative",
    backgroundColor: "none",
    border: "none",
    fontSize: "var(--font-size-normal)",
  },
}));

// Varied line widths so placeholder rows read like real dialogue text.
const SKELETON_LINE_WIDTHS = [
  ["92%", "78%", "55%"],
  ["85%", "64%"],
  ["96%", "88%", "71%"],
  ["74%", "58%"],
  ["90%", "82%", "40%"],
];

// Skeleton rows shown in place of the dialogues while a page loads.
const DialogueTableSkeletonRows = ({ rowCount }) =>
  Array.from({ length: rowCount }, (_, rowIndex) => {
    const lines = SKELETON_LINE_WIDTHS[rowIndex % SKELETON_LINE_WIDTHS.length];
    const translationLines =
      SKELETON_LINE_WIDTHS[(rowIndex + 2) % SKELETON_LINE_WIDTHS.length];

    return (
      <tr key={rowIndex} className={classes["AudioWorkTableRow"]}>
        <td
          className={`${classes["AudioWorkMetaColumn"]} ${classes["AudioWorkTableColumnCharacter"]}`}
        >
          <Grid container direction="column" gap={1.5} alignItems="center">
            <Skeleton
              variant="rounded"
              width="80%"
              height={25}
              sx={{ bgcolor: "rgba(255, 255, 255, 0.2)" }}
            />
            <Skeleton
              variant="text"
              width="60%"
              sx={{ bgcolor: "rgba(255, 255, 255, 0.2)" }}
            />
          </Grid>
        </td>
        <td
          className={`${classes["AudioWorkDataColumn"]} ${classes["AudioWorkTableColumnOriginal"]}`}
        >
          {lines.map((width, lineIndex) => (
            <Skeleton key={lineIndex} variant="text" width={width} />
          ))}
          <Skeleton variant="rounded" height={32} sx={{ mt: 1.5 }} />
        </td>
        <td
          className={`${classes["AudioWorkDataColumn"]} ${classes["AudioWorkTableColumnTranslation"]}`}
        >
          {translationLines.map((width, lineIndex) => (
            <Skeleton key={lineIndex} variant="text" width={width} />
          ))}
        </td>
        <td
          className={`${classes["AudioWorkDataColumn"]} ${classes["AudioWorkTableColumnNewAudio"]}`}
        >
          <Grid container gap={1}>
            <Skeleton variant="rounded" width={90} height={28} />
            <Skeleton variant="rounded" width={70} height={28} />
          </Grid>
          <Skeleton variant="rounded" height={32} sx={{ mt: 1.5 }} />
        </td>
        <td className={classes["AudioWorkDataColumn"]}>
          <Skeleton variant="text" width={80} />
          <Skeleton variant="text" width={80} />
        </td>
        <td className={classes["AudioWorkDataColumn"]}>
          <Skeleton variant="rounded" height={48} />
          <Skeleton variant="text" width={110} sx={{ mt: 1 }} />
        </td>
      </tr>
    );
  });

const VideoPerformanceDirection = () => {
  const params = useParams();
  const { language: languageCode } = params;
  const screenIsOver1200px = useMediaQuery("(min-width: 1200px)");
  const translatedAudioRefs = useRef([]);
  const originalAudioRefs = useRef([]);

  const { state } = useContext(Store);
  const { projectFile, userInfo } = state;

  const videoReference =
    projectFile?.videoUrl || projectFile?.fileUrl || projectFile?.video || "";
  const { playbackUrl: videoUrl } = useProjectMediaPlayback({
    reference: videoReference,
    projectId: projectFile?.id,
  });

  const [characters, setCharacters] = useState([]);
  const [audioData, setAudioData] = useState([]);
  useDialoguePlaybackRefresh({
    dialogues: audioData,
    projectId: projectFile?.id,
    setDialogues: setAudioData,
  });
  const [currentTime, setCurrentTime] = useState(0);
  const [seekTime, setSeekTime] = useState(0);
  const [seekTimeIndex, setSeekTimeIndex] = useState(0);
  const [scrollableItem, setScrollableItem] = useState(0);
  const [scrollableItemLastUpdated, setScrollableItemLastUpdated] = useState(0);
  const [audioLoader, setAudioLoader] = useState(false);
  const [loader, setLoader] = useState(false);
  const [versionName, setVersionName] = useState("");
  const [outputAudioLayout, setOutputAudioLayout] = useState(
    DEFAULT_OUTPUT_AUDIO_LAYOUT
  );
  const [videoSource, setVideoSource] = useState(DEFAULT_VIDEO_SOURCE);
  // Whether the project has an original stored; until the versions
  // endpoint says so, only the lower-resolution copy can be chosen.
  const [hasSourceVideo, setHasSourceVideo] = useState(false);

  useEffect(() => {
    if (!projectFile?.id) {
      return undefined;
    }
    let cancelled = false;
    axios
      .post(GET_ALL_QUALITYASSURANCE_VERSIONS, { projectId: projectFile.id })
      .then((response) => {
        if (!cancelled) {
          setHasSourceVideo(Boolean(response.data.hasSourceVideo));
        }
      })
      .catch((error) => console.log(error));
    return () => {
      cancelled = true;
    };
  }, [projectFile?.id]);

  const [syncPreview, setSyncPreview] = useState(false);
  // Listening volume for dialogue audio during preview; not saved to the dialogue.
  const [previewVolume, setPreviewVolume] = useState(1);
  const [continuePreviewAcrossPages, setContinuePreviewAcrossPages] =
    useState(false);
  const [pendingContinuousPreviewPage, setPendingContinuousPreviewPage] =
    useState(null);
  const [extendedPreviewDialogueIndex, setExtendedPreviewDialogueIndex] =
    useState(null);
  const [completedPreviewDialogueIndex, setCompletedPreviewDialogueIndex] =
    useState(null);
  const [pendingPreviewDialogueIndex, setPendingPreviewDialogueIndex] =
    useState(null);
  const [previewWaitingForVideo, setPreviewWaitingForVideo] = useState(false);

  const [playPausePlayer, setPlayPausePlayer] = useState("pause");
  const [manualPauseTick, setManualPauseTick] = useState(0);
  const [currentlyPlayingId, setCurrentlyPlayingId] = useState(null);

  const numberOfDialogues = projectFile?.numberOfDialogues || 0;

  const timeoutIdRef = useRef(null);

  const [globalEmotions, setGlobalEmotions] = useState({});
  const experimentalTraining = useExperimentalFeature(
    projectFile?.id,
    userInfo?.id,
    projectFile?.name
  );

  const [pageButtonActivity, setPageButtonActivity] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [goToPageNumber, setGoToPageNumber] = useState("");

  const [dialogueSearchFilters, setDialogueSearchFilters] = useState(
    DEFAULT_DIALOGUE_SEARCH_FILTERS
  );
  const [isDialogueSearchActive, setIsDialogueSearchActive] = useState(false);
  const [searchResultCount, setSearchResultCount] = useState(0);

  const [inputStartTime, setInputStartTime] = useState({});
  const [inputEndTime, setInputEndTime] = useState({});
  const [subtitleOnlyMap, setSubtitleOnlyMap] = useState({});
  const [vEngineTypeMap, setVEngineTypeMap] = useState({});
  const [performanceCollapsedMap, setPerformanceCollapsedMap] = useState({});
  const [speedSelectionMap, setSpeedSelectionMap] = useState({});
  const [audioAutoPlayRequestMap, setAudioAutoPlayRequestMap] = useState({});
  // Lines picked for a bulk action, by dbid; only lines on the current page.
  const [selectedDialogueIds, setSelectedDialogueIds] = useState(
    () => new Set()
  );

  useEffect(() => {
    setSelectedDialogueIds(new Set());
  }, [pageNumber, dialogueSearchFilters, isDialogueSearchActive, languageCode]);

  const itemsPerPage = 10;

  const activeDialogueCount = isDialogueSearchActive
    ? searchResultCount
    : numberOfDialogues;

  const totalPages = Math.max(1, Math.ceil(activeDialogueCount / itemsPerPage));

  const containerRef = useRef(null);
  const performanceToggleButtonWidth = "100px";

  useEffect(() => {
    const fetchEmotions = async () => {
      if (!experimentalTraining) return;

      try {
        const db = getFirestore();
        const emotionsRef = doc(db, "EmotionMappings", "global-dec-2024");
        const emotionsSnap = await getDoc(emotionsRef);

        if (emotionsSnap.exists()) {
          setGlobalEmotions(emotionsSnap.data());
        } else {
          console.log("No emotion mappings found");
        }
      } catch (err) {
        console.error("Error fetching emotion mappings:", err);
      }
    };

    fetchEmotions();
  }, [experimentalTraining]);

  const scrollToItem = (index) => {
    const manpIndex = index;

    if (containerRef.current) {
      const item = containerRef.current.children[manpIndex];
      if (item) {
        item.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  const displayAudioTime = useCallback((data) => {
    setCurrentTime(data);
  }, []);

  function findDisplayName(speaker) {
    return getSpeakerDisplayName(characters, speaker);
  }

  const originalAudioEmotionOptions = useMemo(() => {
    const emotionsFromLoadedDialogues = audioData
      .map((dialogue) => dialogue?.originalAudioEmotion)
      .filter(Boolean);

    const emotionsFromCharacterVoices = characters
      .flatMap((character) => character?.voices || [])
      .map((voice) => voice?.emotion)
      .filter(Boolean);

    return [
      ...new Set([
        "neutral",
        ...emotionsFromLoadedDialogues,
        ...emotionsFromCharacterVoices,
      ]),
    ];
  }, [audioData, characters]);

  const checkTimeInRange = useCallback(
    (currentTimeValue) => {
      if (!audioData) {
        return;
      }

      const numericTime = Number(currentTimeValue);

      if (Number.isNaN(numericTime)) {
        return;
      }

      let itemInTimeRange = null;

      for (const item of audioData) {
        const start = Number(item.start);
        const end = Number(item.end);

        if (Number.isNaN(start) || Number.isNaN(end)) {
          continue;
        }

        if (numericTime >= start && numericTime < end) {
          if (!itemInTimeRange || Number(itemInTimeRange.start) < start) {
            itemInTimeRange = item;
          }
        }
      }

      if (itemInTimeRange) {
        if (itemInTimeRange.id !== scrollableItemLastUpdated) {
          setScrollableItemLastUpdated(itemInTimeRange.id);
          setScrollableItem(itemInTimeRange.id);
        }
      }
    },
    [audioData, scrollableItemLastUpdated]
  );

  useEffect(() => {
    checkTimeInRange(currentTime);
  }, [currentTime, checkTimeInRange]);

  useEffect(() => {
    if (false) {
      if (screenIsOver1200px) {
        scrollToItem(scrollableItem - 1);
      }
    }
  }, [scrollableItem, screenIsOver1200px]);

  const findDefaultEmotion = useCallback(
    (speaker, originalAudioEmotion) => {
      if (experimentalTraining) {
        console.log(
          `used experimental ${globalEmotions[originalAudioEmotion]}`
        );
        return (
          globalEmotions[originalAudioEmotion] ||
          globalEmotions["neutral"] ||
          "dwo0oupDDyJeTBpyE6n3"
        );
      }

      console.log("didn't use experimental");

      return getDefaultVoiceIdForEmotion(
        projectFile?.characters,
        speaker,
        originalAudioEmotion
      );
    },
    [experimentalTraining, globalEmotions, projectFile?.characters]
  );

  function checkTranslationLength(originalLength, translatedLength) {
    return getTranslationDurationMessage(originalLength, translatedLength);
  }

  function getRecommendedSpeedOption(originalLength, translatedLength) {
    return getProjectWorkRecommendedSpeedOption(originalLength, translatedLength);
  }

  const checkLanguage = useCallback(
    (translations = []) => {
      return ensureProjectFinalLanguageTranslations(
        translations,
        projectFile?.finalLang
      );
    },
    [projectFile?.finalLang]
  );

  const findTransObjectsbyLangCode = useCallback(
    (array = []) => findTranslationByLanguageCode(array, languageCode),
    [languageCode]
  );

  const prepareDialogues = useCallback(
    (dialogues = []) => {
      const subtitleMap = {};
      const performanceMap = {};
      const engineMap = {};
      const speedMap = {};

      const hydratedDialogues = dialogues.map((dia, index) => {
        const translations = checkLanguage(dia.translations);
        const selectedTranslation = findTransObjectsbyLangCode(translations);

        const initialVoiceEngine =
          selectedTranslation?.translatedAudioLastUsedModel ||
          dia.translatedAudioLastUsedModel ||
          dia.vEngineType ||
          DEFAULT_VOICE_ENGINE;

        subtitleMap[dia.dbid] = dia.subtitleOnly === true;
        performanceMap[dia.dbid] = dia.approved === true;
        engineMap[dia.dbid] = initialVoiceEngine;
        speedMap[dia.dbid] = "";

        return {
          ...dia,
          id: index,
          vEngineType: initialVoiceEngine,
          tempTransEmotion: findDefaultEmotion(
            dia.speaker,
            dia.originalAudioEmotion
          ),
          translatedAudioDurationTemp: null,
          record: false,
          translations,
          filterShow: true,
          loader: false,
          tempAudioList: selectedTranslation?.translatedAudio
            ? [
                {
                  url: selectedTranslation.translatedAudio,
                  objectPath: selectedTranslation.translatedAudioPath,
                  length: selectedTranslation.translatedAudioDuration,
                  emotion: "original",
                },
              ]
            : [],
        };
      });

      setSubtitleOnlyMap(subtitleMap);
      setPerformanceCollapsedMap(performanceMap);
      setVEngineTypeMap(engineMap);
      setSpeedSelectionMap(speedMap);

      return hydratedDialogues;
    },
    [checkLanguage, findDefaultEmotion, findTransObjectsbyLangCode]
  );

  const findEmotionByVoiceId = (voiceId) => {
    if (experimentalTraining) {
      for (let currEmotion in globalEmotions) {
        if (globalEmotions[currEmotion] === voiceId) {
          return currEmotion;
        }
      }
    }

    for (const character of characters) {
      if (!character.voices || character.voices.length === 0) continue;

      for (const voice of character.voices) {
        if (voice.voice_id === voiceId) {
          return voice.emotion;
        }
      }
    }

    return null;
  };

  const handleVoiceOverLapping = (index, translatedDuration) => {
    const dialogue = audioData[index];
    const nextRowOnPage = audioData[index + 1];
    // Unfiltered rows are contiguous, so the next row is the true next line
    // (and reflects timing edits). Otherwise use the backend's nextStart,
    // which covers lines on the next page or hidden by the active filters.
    const nextStart =
      !isDialogueSearchActive && nextRowOnPage
        ? nextRowOnPage.start
        : dialogue?.nextStart ?? nextRowOnPage?.start;

    return isTranslatedAudioOverlapping(
      dialogue?.start,
      translatedDuration,
      nextStart
    ) ? (
      <span className={classes["DialogueRowStatusTag"]}>Overlapping</span>
    ) : (
      ""
    );
  };

  const handleSpeakerChange = async (e, index) => {
    setAudioLoader(true);

    const updatedData = [...audioData];

    updatedData[index] = {
      ...updatedData[index],
      speaker: e.target.value,
    };

    setAudioData(updatedData);

    try {
      const response = await axios.post(UPDATE_ONE_SPEECH, {
        projectId: projectFile.id,
        dialogue: updatedData[index],
      });

      if (response.data) {
        setAudioData(updatedData);
        setAudioLoader(false);
      }
    } catch (err) {
      console.log(err);
      setAudioLoader(false);
    }
  };

  const formatTime = (timeInSeconds) => {
    const hours = Math.floor(timeInSeconds / 3600);
    const minutes = Math.floor((timeInSeconds % 3600) / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    const milliseconds = Math.round((timeInSeconds % 1) * 100);

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:${String(seconds).padStart(2, "0")}.${String(milliseconds).padStart(
      2,
      "0"
    )}`;
  };

  useEffect(() => {
    const initialStartTimes = {};
    const initialEndTimes = {};

    audioData.forEach((data, index) => {
      initialStartTimes[index] = formatTime(data.start || 0);
      initialEndTimes[index] = formatTime(data.end || 0);
    });

    setInputStartTime(initialStartTimes);
    setInputEndTime(initialEndTimes);
  }, [audioData]);

  const saveStartTime = useMemo(
    () =>
      debounce(async (data) => {
        try {
          await axios.post(UPDATE_ONE_SPEECH, {
            projectId: projectFile?.id,
            dialogue: data,
          });
        } catch (error) {
          console.error("Error saving start time:", error);
        }
      }, 500),
    [projectFile?.id]
  );

  const saveEndTime = useMemo(
    () =>
      debounce(async (data) => {
        try {
          await axios.post(UPDATE_ONE_SPEECH, {
            projectId: projectFile?.id,
            dialogue: data,
          });
        } catch (error) {
          console.error("Error saving end time:", error);
        }
      }, 500),
    [projectFile?.id]
  );

  useEffect(() => {
    return () => {
      saveStartTime.cancel?.();
      saveEndTime.cancel?.();
    };
  }, [saveEndTime, saveStartTime]);

  const handleStartTimeChange = (e, index) => {
    const value = e.target.value;

    setInputStartTime((prev) => ({ ...prev, [index]: value }));

    const newTime = parseTimestampAsTotalSeconds(value);
    const updatedData = [...audioData];

    updatedData[index] = { ...updatedData[index], start: newTime };

    setAudioData(updatedData);
    saveStartTime(updatedData[index]);
  };

  const handleEndTimeChange = (e, index) => {
    const value = e.target.value;

    setInputEndTime((prev) => ({ ...prev, [index]: value }));

    const newTime = parseTimestampAsTotalSeconds(value);
    const updatedData = [...audioData];

    updatedData[index] = { ...updatedData[index], end: newTime };

    setAudioData(updatedData);
    saveEndTime(updatedData[index]);
  };

  const getDialogueDurationSeconds = (dialogue) => {
    if (!dialogue) {
      return Number.NaN;
    }

    const rawDuration = Number(dialogue.duration);

    if (!Number.isNaN(rawDuration) && rawDuration > 0) {
      return rawDuration;
    }

    const startSeconds =
      typeof dialogue.start === "number"
        ? dialogue.start
        : parseTimestampAsTotalSeconds(dialogue.start) || 0;

    const endSeconds =
      typeof dialogue.end === "number"
        ? dialogue.end
        : parseTimestampAsTotalSeconds(dialogue.end) || 0;

    const computedDuration = endSeconds - startSeconds;

    return computedDuration > 0 ? computedDuration : Number.NaN;
  };

  function handleNoteChange(e, index) {
    const updatedData = [...audioData];

    updatedData[index].translations = updatedData[index].translations.map(
      (prev) => {
        if (prev.languageCode === languageCode) {
          return {
            ...prev,
            translatedAudioNote: e.target.value,
          };
        }

        return prev;
      }
    );

    clearTimeout(timeoutIdRef.current);

    timeoutIdRef.current = setTimeout(async () => {
      await axios.post(UPDATE_ONE_SPEECH, {
        projectId: projectFile.id,
        dialogue: updatedData[index],
      });
    }, 1650);

    setAudioData(updatedData);
  }

  function handleTextChange(e, index) {
    const updatedData = [...audioData];

    updatedData[index] = {
      ...updatedData[index],
      text: e.target.value,
    };

    setAudioData(updatedData);
    clearTimeout(timeoutIdRef.current);

    timeoutIdRef.current = setTimeout(async () => {
      await axios.post(UPDATE_ONE_SPEECH, {
        projectId: projectFile.id,
        dialogue: updatedData[index],
      });
    }, 1650);
  }

  function handleTranslationChange(e, index) {
    const updatedData = [...audioData];

    updatedData[index].translations = updatedData[index].translations.map(
      (prev) => {
        if (prev.languageCode === languageCode) {
          return {
            ...prev,
            translatedText: e.target.value,
          };
        }

        return prev;
      }
    );

    setAudioData(updatedData);
    clearTimeout(timeoutIdRef.current);

    timeoutIdRef.current = setTimeout(async () => {
      await axios.post(UPDATE_ONE_SPEECH, {
        projectId: projectFile.id,
        dialogue: updatedData[index],
      });
    }, 1650);
  }

  function handleTextFocus(index, startTime) {
    const startSeconds =
      typeof startTime === "number"
        ? startTime
        : parseTimestampAsTotalSeconds(startTime) || 0;

    setSeekTime(startSeconds);
    setSeekTimeIndex((prev) => prev + 1);

    const originalPlayer = originalAudioRefs.current[index];

    if (originalPlayer?.audioEl?.current) {
      originalPlayer.audioEl.current.pause();
      originalPlayer.audioEl.current.currentTime = 0;
    }

    const translatedPlayer = translatedAudioRefs.current[index];

    if (translatedPlayer) {
      if (typeof translatedPlayer.pause === "function") {
        translatedPlayer.pause();
      }

      if (typeof translatedPlayer.seek === "function") {
        translatedPlayer.seek(0);
      }
    }

    if (!syncPreview) {
      requestSyncPause();
      setManualPauseTick((prev) => prev + 1);
      return;
    }

    const shouldResumePlayback = playPausePlayer === "play";

    if (!shouldResumePlayback) {
      requestSyncPause();
      setPlayPausePlayer("pause");
      return;
    }

    if (
      currentlyPlayingId !== null &&
      currentlyPlayingId !== undefined &&
      playRow(index)
    ) {
      setPlayPausePlayer("play");
      return;
    }

    requestSyncPlay();
  }

  const handleDialogueRating = async (rating, index) => {
    setAudioLoader(true);

    const updatedData = [...audioData];

    updatedData[index].translations = updatedData[index].translations.map(
      (prev) => {
        if (prev.languageCode === languageCode) {
          return {
            ...prev,
            translatedAudioRating: rating,
          };
        }

        return prev;
      }
    );

    try {
      const response = await axios.post(UPDATE_ONE_SPEECH, {
        projectId: projectFile.id,
        dialogue: updatedData[index],
      });

      if (response.data) {
        setAudioData(updatedData);
        setAudioLoader(false);
      }
    } catch (err) {
      console.log(err);
      setAudioLoader(false);
    }
  };

  // The spinner replaces the +1/+3 button, so it must be cleared whenever a
  // request stops before it is sent, or the button would stay hidden.
  const clearDialogueLoader = (index) => {
    setAudioData((prevData) => {
      const updatedData = [...prevData];
      updatedData[index].loader = false;
      return updatedData;
    });
  };

  const handleNewAudioUrl = async (index) => {
    const now = new Date();
    const storedVoiceEngine =
      vEngineTypeMap[audioData[index].dbid] || DEFAULT_VOICE_ENGINE;
    if (isLegacyVoiceEngine(storedVoiceEngine)) {
      toast.error(
        "Legacy audio remains playable, but generation with this legacy engine is no longer available. Select a supported voice engine to create new audio."
      );
      clearDialogueLoader(index);
      return;
    }

    const selectedVoiceEngine = getSupportedVoiceEngine(storedVoiceEngine);

    const isSoundEffectEngine =
      selectedVoiceEngine === SOUND_EFFECT_VOICE_ENGINE;

    if (!isSoundEffectEngine && audioData[index].tempTransEmotion === "") {
      toast.error("Error !! Choose an emotion");
      clearDialogueLoader(index);
      return;
    }

    const dialogueCopy = {
      ...audioData[index],
      translatedText: findTransObjectsbyLangCode(audioData[index].translations)
        .translatedText,
      languageCode: languageCode,
      loader: false,
      vEngineType: selectedVoiceEngine,
    };

    if (!isSoundEffectEngine) {
      dialogueCopy.voiceId = audioData[index].tempTransEmotion;
      dialogueCopy.emotion = findEmotionByVoiceId(
        audioData[index].tempTransEmotion
      );
    } else {
      dialogueCopy.voiceId = null;
      dialogueCopy.emotion = null;
    }

    const requestBody = isSoundEffectEngine
      ? {
          projectId: projectFile.id,
          dialogueID: audioData[index].dbid,
          prompt_influence: 0.5,
          vEngineType: SOUND_EFFECT_VOICE_ENGINE,
          dialogue: dialogueCopy,
        }
      : {
          dialogue: dialogueCopy,
          projectName: projectFile.name,
          creationDate: projectFile.creationDate,
          projectId: projectFile.id,
          vEngineType: selectedVoiceEngine,
        };

    const hasRequiredFields = isSoundEffectEngine
      ? requestBody.dialogue && requestBody.projectId && requestBody.dialogueID
      : requestBody.dialogue &&
        requestBody.projectId &&
        requestBody.projectName;

    if (!hasRequiredFields) {
      toast.error("Missing data required to create new audio");
      clearDialogueLoader(index);
      return;
    }

    try {
      const endpoint = isSoundEffectEngine
        ? GENERATE_SOUND_EFFECT
        : CREATE_NEW_AUDIO;

      const headers = await injectToken();
      console.log("Headers:", headers);

      // const response = await axios.post(endpoint, requestBody);
      const response = await axios.post(endpoint, requestBody, {
        headers,
      });
      // const response = await fetch(endpoint, {
      //                 method: "POST",
      //                 headers: await injectToken(userInfo.id),
      //                 body: JSON.stringify(requestBody),
      //             });

      const generatedAudio = readAudioClientResponse(response.data);
      const durableAudioReference =
        generatedAudio.objectPath || generatedAudio.playbackUrl;
      if (!durableAudioReference || !generatedAudio.playbackUrl) {
        throw new Error("Audio generation response is missing media access data");
      }

      const newAudioObject = {
        url: generatedAudio.playbackUrl,
        objectPath: generatedAudio.objectPath,
        creationTime: now,
        emotion: isSoundEffectEngine ? null : audioData[index].tempTransEmotion,
        translatedAudioDuration: generatedAudio.durationSeconds,
      };

      const persistedTranslations = dialogueCopy.translations.map((prev) => {
        if (prev.languageCode === languageCode) {
          const persistedVersion = {
            ...newAudioObject,
            url: durableAudioReference,
          };
          return {
            ...prev,
            translatedAudio: durableAudioReference,
            ...(generatedAudio.objectPath
              ? { translatedAudioPath: generatedAudio.objectPath }
              : {}),
            translatedAudioDuration: newAudioObject.translatedAudioDuration,
            translatedAudioList: prev.translatedAudioList
              ? [...prev.translatedAudioList, persistedVersion]
              : [persistedVersion],
          };
        }

        return prev;
      });

      const dialogueToPersist = {
        ...dialogueCopy,
        translations: persistedTranslations,
      };

      await axios.post(UPDATE_ONE_SPEECH, {
        projectId: projectFile.id,
        dialogue: dialogueToPersist,
      });

      const localDialogue = {
        ...dialogueCopy,
        translations: persistedTranslations.map((translation) =>
          translation.languageCode === languageCode
            ? {
                ...translation,
                translatedAudio: generatedAudio.playbackUrl,
                translatedAudioPath: generatedAudio.objectPath,
              }
            : translation
        ),
      };

      const newSingleAudio = {
        url: generatedAudio.playbackUrl,
        objectPath: generatedAudio.objectPath,
        length: generatedAudio.durationSeconds,
        emotion: generatedAudio.emotion,
      };

      setAudioData((prevData) => {
        const updatedData = [...prevData];

        const updatedTempAudioList = [
          ...updatedData[index].tempAudioList,
          newSingleAudio,
        ];

        updatedData[index] = {
          ...localDialogue,
          tempAudioList: updatedTempAudioList,
        };

        return updatedData;
      });

      setAudioAutoPlayRequestMap((previousRequests) => ({
        ...previousRequests,
        [audioData[index].dbid]:
          (previousRequests[audioData[index].dbid] || 0) + 1,
      }));

      toast.success("New Audio has been added", {
        hideProgressBar: false,
        autoClose: 1000,
      });
    } catch (err) {
      console.error(err);
      toast.error("Error in creating new audio");

      setAudioData((prevData) => {
        const updatedData = [...prevData];
        updatedData[index].loader = false;
        return updatedData;
      });
    }
  };

  const handleMultipleAudio = async (index) => {
    if (audioData[index].tempTransEmotion === "") {
      toast.error("Error !! Choose an emotion");
      clearDialogueLoader(index);
      return;
    }

    const storedVoiceEngine =
      vEngineTypeMap[audioData[index].dbid] || DEFAULT_VOICE_ENGINE;
    if (isLegacyVoiceEngine(storedVoiceEngine)) {
      toast.error(
        "Legacy audio remains playable, but generation with this legacy engine is no longer available. Select a supported voice engine to create multiple takes."
      );
      clearDialogueLoader(index);
      return;
    }

    const selectedVoiceEngine = getSupportedVoiceEngine(storedVoiceEngine);

    if (selectedVoiceEngine === SOUND_EFFECT_VOICE_ENGINE) {
      toast.error(
        "Multiple takes are not available for the selected voice engine"
      );
      clearDialogueLoader(index);
      return;
    }

    const dialogueCopy = {
      ...audioData[index],
      translatedText: findTransObjectsbyLangCode(audioData[index].translations)
        .translatedText,
      voiceId: audioData[index].tempTransEmotion,
      languageCode: languageCode,
      emotion: findEmotionByVoiceId(audioData[index].tempTransEmotion),
      loader: false,
      vEngineType: selectedVoiceEngine,
    };

    const targetDurationSeconds = getDialogueDurationSeconds(dialogueCopy);

    const requestBody = {
      dialogue: dialogueCopy,
      projectName: projectFile.name,
      creationDate: projectFile.creationDate,
      projectId: projectFile.id,
      targetDuration: targetDurationSeconds,
      vEngineType: selectedVoiceEngine,
    };

    if (
      !requestBody.dialogue ||
      !requestBody.projectId ||
      !requestBody.projectName
    ) {
      toast.error("Missing data required to create multiple audio takes");
      clearDialogueLoader(index);
      return;
    }

    try {
      const headers = await injectToken();

      const response = await axios.post(CREATE_MULTIPLE_AUDIO, requestBody, {
        headers,
      });
      const generatedAudios = response.data.map((item) => {
        const generated = readAudioClientResponse(item);
        return {
          ...item,
          url: generated.playbackUrl,
          objectPath: generated.objectPath,
          length: generated.durationSeconds,
          emotion: generated.emotion,
        };
      });

      setAudioData((prevData) => {
        const updatedData = [...prevData];
        const existingAudioList = updatedData[index].tempAudioList;

        const newAudioList = generatedAudios.filter(
          (newAudio) =>
            !existingAudioList.some(
              (existingAudio) => existingAudio.url === newAudio.url
            )
        );

        updatedData[index].tempAudioList = [
          ...existingAudioList,
          ...newAudioList,
        ];
        updatedData[index].loader = false;

        return updatedData;
      });

      toast.success("New audio list has been added", {
        hideProgressBar: false,
        autoClose: 1000,
      });
    } catch (err) {
      console.error(err);
      toast.error("Error in creating new audio");

      setAudioData((prevData) => {
        const updatedData = [...prevData];
        updatedData[index].loader = false;
        return updatedData;
      });
    }
  };

  const selectAudioOption = async (e, index, playerIndex) => {
    const now = new Date();
    const selectedAudioReference = e.target.value;

    if (selectedAudioReference === "") {
      toast.error("Select an audio!");
      return;
    }

    const selectedAudio = audioData[index].tempAudioList.find(
      (audio) =>
        getGeneratedAudioOptionValue(audio) === selectedAudioReference
    );
    if (!selectedAudio) {
      toast.error("The selected audio is no longer available");
      return;
    }

    const audioUrl = selectedAudio.url;
    const audioDuration = selectedAudio.length;
    const objectPath = selectedAudio.objectPath || audioUrl;

    const dialogueCopy = audioData[index];

    const newAudioObject = {
      url: audioUrl,
      translatedAudioDuration: audioDuration,
      creationTime: now,
      emotion: audioData[index].tempTransEmotion,
    };

    const persistedTranslations = dialogueCopy.translations.map((prev) => {
      if (prev.languageCode === languageCode) {
        return {
          ...prev,
          translatedAudio: objectPath,
          translatedAudioPath: selectedAudio.objectPath || null,
          translatedAudioDuration: newAudioObject.translatedAudioDuration,
        };
      }

      return prev;
    });

    const dialogueToPersist = {
      ...dialogueCopy,
      translations: persistedTranslations,
    };
    const localDialogue = {
      ...dialogueCopy,
      translations: persistedTranslations.map((translation) =>
        translation.languageCode === languageCode
          ? {
              ...translation,
              translatedAudio: audioUrl,
            }
          : translation
      ),
    };

    try {
      await axios.post(UPDATE_ONE_SPEECH, {
        projectId: projectFile.id,
        dialogue: dialogueToPersist,
      });

      setAudioData((prevData) => {
        const updatedData = [...prevData];
        updatedData[index] = localDialogue;
        return updatedData;
      });

      setTimeout(() => {
        if (translatedAudioRefs.current[playerIndex]) {
          translatedAudioRefs.current[playerIndex].seek(0);
          translatedAudioRefs.current[playerIndex].play();
        }
      }, 1000);

      toast.info(" Audio has been updated", {
        hideProgressBar: false,
        autoClose: 1000,
      });
    } catch (error) {
      console.error(error);

      setAudioData((prevData) => {
        const updatedData = [...prevData];
        updatedData[index].loader = false;
        return updatedData;
      });
    }
  };

  const handleAdjustSpeed = async (dialogue, requestedSpeed) => {
    if (!dialogue) {
      toast.error("Dialogue details are not available.");
      return;
    }

    if (!projectFile?.name) {
      toast.error("Project name is required to adjust audio speed.");
      return;
    }

    const selectedSpeed =
      requestedSpeed || speedSelectionMap[dialogue.dbid] || "auto";

    const dialogueWithLanguageCode = {
      ...dialogue,
      languageCode: dialogue.languageCode || languageCode,
    };

    try {
      setAudioLoader(true);

      let response;
      const headers = await injectToken();

      if (selectedSpeed === "auto") {
        response = await axios.post(
          ADJUST_AUDIO_SPEED_AUTO,
          {
            dialogue: dialogueWithLanguageCode,
            projectId: projectFile.id,
            projectName: projectFile.name,
          },
          { headers }
        );
      } else {
        response = await axios.post(
          ADJUST_AUDIO_SPEED,
          {
            dialogue: dialogueWithLanguageCode,
            projectId: projectFile.id,
            projectName: projectFile.name,
            speed: selectedSpeed,
          },
          { headers }
        );
      }

      const adjustedAudio = readAudioClientResponse(response?.data);
      const adjustedAudioUrl = adjustedAudio.playbackUrl;
      const durableAudioReference =
        adjustedAudio.objectPath || adjustedAudio.playbackUrl;
      let dialogueToPersist = dialogue;

      if (adjustedAudioUrl) {
        const adjustedAudioEntry = {
          url: adjustedAudioUrl,
          objectPath: adjustedAudio.objectPath,
          length: adjustedAudio.durationSeconds,
          emotion: `${selectedSpeed}x`,
        };
        dialogueToPersist = {
          ...dialogue,
          translations: dialogue.translations.map((translation) =>
            translation.languageCode === languageCode
              ? {
                  ...translation,
                  translatedAudio: durableAudioReference,
                  ...(adjustedAudio.objectPath
                    ? { translatedAudioPath: adjustedAudio.objectPath }
                    : {}),
                  translatedAudioDuration:
                    adjustedAudioEntry.length ||
                    translation.translatedAudioDuration,
                }
              : translation
          ),
        };

        setAudioData((prevData) => {
          const updatedData = prevData.map((row) => {
            if (row.dbid !== dialogue.dbid) {
              return row;
            }

            const updatedTempAudioList = row.tempAudioList.some(
              (audio) => audio.url === adjustedAudioEntry.url
            )
              ? row.tempAudioList
              : [...row.tempAudioList, adjustedAudioEntry];

            const updatedTranslations = row.translations.map((translation) =>
              translation.languageCode === languageCode
                ? {
                    ...translation,
                    translatedAudio: adjustedAudioUrl,
                    ...(adjustedAudio.objectPath
                      ? { translatedAudioPath: adjustedAudio.objectPath }
                      : {}),
                    translatedAudioDuration:
                      adjustedAudioEntry.length ||
                      translation.translatedAudioDuration,
                  }
                : translation
            );

            return {
              ...row,
              tempAudioList: updatedTempAudioList,
              translations: updatedTranslations,
            };
          });

          return updatedData;
        });
      }

      await axios.post(UPDATE_ONE_SPEECH, {
        projectId: projectFile.id,
        dialogue: dialogueToPersist,
      });

      toast.success(
        `Requested ${
          selectedSpeed === "auto" ? "automatic" : `${selectedSpeed}x`
        } speed adjustment.`
      );
    } catch (error) {
      console.error("Error adjusting audio speed:", error);
      toast.error(
        error.response?.data?.message || "Failed to adjust audio speed."
      );
    } finally {
      setAudioLoader(false);
    }
  };

  const handleTranslatedAudioVolume = async (e, index) => {
    const dialogueCopy = audioData[index];

    let volume;
    const data = e.target.value;

    if (data === null || data === "") {
      volume = null;
    } else if (isNaN(data)) {
      volume = null;
    } else {
      volume = Number(data);
    }

    dialogueCopy.translations = dialogueCopy.translations.map((prev) => {
      if (prev.languageCode === languageCode) {
        return {
          ...prev,
          translatedAudioVolume: volume,
        };
      }

      return prev;
    });

    try {
      await axios.post(UPDATE_ONE_SPEECH, {
        projectId: projectFile.id,
        dialogue: dialogueCopy,
      });

      setAudioData((prevData) => {
        const updatedData = [...prevData];
        updatedData[index] = dialogueCopy;
        return updatedData;
      });

      toast.info(" Audio has been updated", {
        hideProgressBar: false,
        autoClose: 1000,
      });
    } catch (error) {
      console.error(error);

      setAudioData((prevData) => {
        const updatedData = [...prevData];
        return updatedData;
      });
    }
  };

  const handleKeepOriginalText = async (index) => {
    setAudioLoader(true);

    const updatedData = [...audioData];

    updatedData[index] = {
      ...updatedData[index],
      keepOriginalText: !updatedData[index].keepOriginalText,
    };

    setAudioData(updatedData);

    try {
      const response = await axios.post(UPDATE_ONE_SPEECH, {
        projectId: projectFile.id,
        dialogue: updatedData[index],
      });

      if (response.data) {
        setAudioData(updatedData);
        setAudioLoader(false);
      }
    } catch (err) {
      console.log(err);
      setAudioLoader(false);
    }
  };

  const finalSubmission = async () => {
    if (!versionName) {
      toast.error("Enter an version name");
      return;
    }

    try {
      await axios.post(MERGING_FULL_API, {
        projectId: projectFile.id,
        projectName: projectFile.name,
        langCode: languageCode,
        version: versionName,
        emails: [userInfo?.email].filter(Boolean),
        submitterName: userInfo.name,
        outputAudioLayout,
        videoSource,
      });

      toast.success("New version is being processed");
      setVersionName("");
      setOutputAudioLayout(DEFAULT_OUTPUT_AUDIO_LAYOUT);
      setVideoSource(DEFAULT_VIDEO_SOURCE);
    } catch (err) {
      console.log(err);
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Error submitting version"
      );
    }
  };

  const stopCurrentPageAudio = useCallback(() => {
    translatedAudioRefs.current.forEach((audioEl) => {
      if (audioEl?.pause) {
        audioEl.pause();
      }

      if (audioEl?.seek) {
        audioEl.seek(0);
      }
    });

    originalAudioRefs.current.forEach((audioEl) => {
      const player = audioEl?.audioEl?.current;

      if (player) {
        player.pause();
        player.currentTime = 0;
      }
    });

    setCurrentlyPlayingId(null);
    setPlayPausePlayer("pause");
  }, []);

  const getDialoguesFromResponse = (responseData) => {
    return (
      responseData?.projectsArray ||
      responseData?.dialogues ||
      responseData?.results ||
      []
    );
  };

  const getDialogueCountFromResponse = (responseData, fallbackCount) => {
    return Number(
      responseData?.totalCount ??
        responseData?.count ??
        responseData?.numberOfDialogues ??
        fallbackCount ??
        0
    );
  };

  const loadSearchDialoguesPage = useCallback(
    async (filters, targetPageNumber = 1) => {
      if (!projectFile?.id || !userInfo?.id) {
        return;
      }

      setLoader(true);
      setPageButtonActivity(true);

      try {
        stopCurrentPageAudio();

        // Filters only (no text search); the backend evaluates them on live
        // dialogue data for this page's translation language.
        const response = await axios.post(SEARCH_SPEECH_FOR_PROJECT, {
          userId: userInfo.id,
          projectId: projectFile.id,
          pageName: "voiceqa",
          pageNumber: Number(targetPageNumber),
          itemsPerPage,
          languageCode,
          speaker: filters.speaker || "",
          originalAudioEmotion: filters.originalAudioEmotion || "",
          hasTranslatedAudio: filters.hasTranslatedAudio || "",
          hasNote: filters.hasNote || "",
          attention: filters.attention || "",
          translationLanguageCode: languageCode,
        });

        const dialogues = getDialoguesFromResponse(response.data);
        const totalCount = getDialogueCountFromResponse(
          response.data,
          dialogues.length
        );
        const hydratedDialogues = await hydrateDialoguePlaybackUrls(
          dialogues,
          projectFile.id
        );

        setAudioData(prepareDialogues(hydratedDialogues));
        setSearchResultCount(totalCount);
        setIsDialogueSearchActive(hasAnyDialogueSearchFilter(filters));
        setPageNumber(Number(targetPageNumber));
        setGoToPageNumber("");
        return true;
      } catch (err) {
        console.error(err);
        toast.error("Error in searching dialogues");
        return false;
      } finally {
        setLoader(false);
        setPageButtonActivity(false);
      }
    },
    [
      languageCode,
      projectFile?.id,
      prepareDialogues,
      stopCurrentPageAudio,
      userInfo?.id,
    ]
  );

  const loadFirstDialoguePage = useCallback(async () => {
    if (!projectFile?.id || !userInfo?.id) {
      return;
    }

    setLoader(true);

    try {
      stopCurrentPageAudio();

      const response = await axios.post(GET_SPEECH_FOR_PROJECT_BY_PAGE, {
        userId: userInfo.id,
        projectId: projectFile.id,
        action: "first",
        Current: null,
        pageName: "voiceqa",
      });

      const dialogues = await response.data.projectsArray;
      const hydratedDialogues = await hydrateDialoguePlaybackUrls(
        dialogues,
        projectFile.id
      );

      setAudioData(prepareDialogues(hydratedDialogues));
      setPageNumber(1);
      setGoToPageNumber("");
    } catch (err) {
      console.log(err);
      toast.error("Error in getting dialogues");
    } finally {
      setLoader(false);
    }
  }, [prepareDialogues, projectFile?.id, stopCurrentPageAudio, userInfo?.id]);

  const handleDialogueSearch = async (filters) => {
    const normalizedFilters = {
      ...DEFAULT_DIALOGUE_SEARCH_FILTERS,
      ...filters,
      searchText: filters.searchText?.trim() || "",
    };

    setDialogueSearchFilters(normalizedFilters);

    if (!hasAnyDialogueSearchFilter(normalizedFilters)) {
      setIsDialogueSearchActive(false);
      setSearchResultCount(0);
      await loadFirstDialoguePage();
      return;
    }

    await loadSearchDialoguesPage(normalizedFilters, 1);
  };

  const handleClearDialogueSearch = async () => {
    setDialogueSearchFilters(DEFAULT_DIALOGUE_SEARCH_FILTERS);
    setIsDialogueSearchActive(false);
    setSearchResultCount(0);
    await loadFirstDialoguePage();
  };

  const nextPrevPageAPI = useCallback(async (action) => {
    setPageButtonActivity(true);

    const targetPageNumber =
      action === "next" ? Number(pageNumber) + 1 : Number(pageNumber) - 1;

    if (targetPageNumber < 1 || targetPageNumber > totalPages) {
      setPageButtonActivity(false);
      return false;
    }

    if (isDialogueSearchActive) {
      return loadSearchDialoguesPage(
        dialogueSearchFilters,
        targetPageNumber
      );
    }

    try {
      stopCurrentPageAudio();

      const response = await axios.post(GET_SPEECH_FOR_PROJECT_BY_PAGE, {
        userId: userInfo.id,
        projectId: projectFile.id,
        action: action,
        pageName: "voiceqa",
      });

      if (response.data) {
        const dialogues = await response.data.projectsArray;
        const hydratedDialogues = await hydrateDialoguePlaybackUrls(
          dialogues,
          projectFile.id
        );

        setAudioData(prepareDialogues(hydratedDialogues));
        setPageNumber(targetPageNumber);
        setPageButtonActivity(false);
        return true;
      }

      setPageButtonActivity(false);
      return false;
    } catch (err) {
      console.log(err);
      toast.error("Error in getting the next page");
      setPageButtonActivity(false);
      return false;
    }
  }, [
    dialogueSearchFilters,
    isDialogueSearchActive,
    loadSearchDialoguesPage,
    pageNumber,
    prepareDialogues,
    projectFile?.id,
    stopCurrentPageAudio,
    totalPages,
    userInfo?.id,
  ]);

  const goToPageAPI = async (number) => {
    const targetPageNumber = Math.max(1, Number(number) || 1);

    if (isDialogueSearchActive) {
      await loadSearchDialoguesPage(dialogueSearchFilters, targetPageNumber);
      return;
    }

    setLoader(true);

    try {
      stopCurrentPageAudio();

      const response = await axios.post(GET_SPEECH_FOR_PROJECT_BY_PAGE, {
        userId: userInfo.id,
        projectId: projectFile.id,
        action: "goto",
        pageNumber: targetPageNumber,
        pageName: "voiceqa",
      });

      if (response.data) {
        const dialogues = await response.data.projectsArray;
        const hydratedDialogues = await hydrateDialoguePlaybackUrls(
          dialogues,
          projectFile.id
        );

        setAudioData(prepareDialogues(hydratedDialogues));
        setGoToPageNumber("");
        setPageNumber(targetPageNumber);
        setLoader(false);
      }
    } catch (err) {
      setLoader(false);
      console.log(err);
      toast.error("Error in getting the next page");
    }
  };

  const bulkSpeed = useBulkSpeedRun({
    projectId: projectFile?.id,
    onFinished: (run) => {
      // Reload the page so adjusted lines play their new audio.
      goToPageAPI(pageNumber);
      const { adjusted, skipped, failed, stillFlagged } =
        summarizeBulkSpeedRun(run);
      const summary =
        `Speed adjustment finished: ${adjusted} adjusted, ${skipped} skipped, ${failed} failed.` +
        (stillFlagged ? ` ${stillFlagged} still need a fix.` : "");
      if (run.state === "failed") {
        toast.error(run.error?.detail || summary);
      } else if (failed) {
        toast.warning(summary);
      } else {
        toast.success(summary);
      }
    },
  });
  const bulkSpeedPendingIds = useMemo(
    () => pendingBulkSpeedLineIds(bulkSpeed.run),
    [bulkSpeed.run]
  );

  const selectableDialogueIds = useMemo(
    () =>
      (audioData || [])
        .filter((dialogue) =>
          isBulkSpeedSelectable(dialogue, languageCode, subtitleOnlyMap)
        )
        .map((dialogue) => dialogue.dbid),
    [audioData, languageCode, subtitleOnlyMap]
  );
  const selectedOnPageCount = selectableDialogueIds.filter((dbid) =>
    selectedDialogueIds.has(dbid)
  ).length;

  const toggleDialogueSelection = (dbid) => {
    setSelectedDialogueIds((prev) => {
      const next = new Set(prev);
      if (next.has(dbid)) {
        next.delete(dbid);
      } else {
        next.add(dbid);
      }
      return next;
    });
  };

  const togglePageSelection = () => {
    setSelectedDialogueIds(
      selectedOnPageCount === selectableDialogueIds.length
        ? new Set()
        : new Set(selectableDialogueIds)
    );
  };

  const selectFlaggedDialogues = () => {
    setSelectedDialogueIds(
      new Set(
        (audioData || [])
          .filter(
            (dialogue) =>
              isBulkSpeedSelectable(dialogue, languageCode, subtitleOnlyMap) &&
              isDurationFlagged(dialogue, languageCode)
          )
          .map((dialogue) => dialogue.dbid)
      )
    );
  };

  const handleBulkSpeedApply = async (speed) => {
    const dialogueIds = selectableDialogueIds.filter((dbid) =>
      selectedDialogueIds.has(dbid)
    );
    if (dialogueIds.length === 0) {
      return;
    }
    const failure = await bulkSpeed.start({ languageCode, dialogueIds, speed });
    if (failure) {
      toast.error(failure);
      return;
    }
    setSelectedDialogueIds(new Set());
  };

  const goToPageAccordingTimeAPI = async (timeOverride) => {
    setLoader(true);

    try {
      stopCurrentPageAudio();

      const effectiveTime =
        typeof timeOverride === "number" && !Number.isNaN(timeOverride)
          ? timeOverride
          : parseFloat(currentTime) || 0;

      const positionResponse = await axios.post(
        GET_SPEECH_POSITION_AROUND_TIME,
        {
          projectId: projectFile.id,
          time: effectiveTime,
        }
      );

      const firstPosition = Math.max(
        1,
        Number(positionResponse.data?.firstPosition) || 1
      );

      const targetPage = Math.max(1, Math.ceil(firstPosition / itemsPerPage));

      const targetScrollableIndex = Math.max(
        0,
        (firstPosition - 1) % itemsPerPage
      );

      const response = await axios.post(GET_SPEECH_FOR_PROJECT_BY_PAGE, {
        userId: userInfo.id,
        projectId: projectFile.id,
        action: "goto",
        pageNumber: targetPage,
        pageName: "voiceqa",
      });

      if (response.data) {
        const dialogues = await response.data.projectsArray;
        const hydratedDialogues = await hydrateDialoguePlaybackUrls(
          dialogues,
          projectFile.id
        );

        setDialogueSearchFilters(DEFAULT_DIALOGUE_SEARCH_FILTERS);
        setIsDialogueSearchActive(false);
        setSearchResultCount(0);

        setAudioData(prepareDialogues(hydratedDialogues));
        setGoToPageNumber("");
        setPageNumber(targetPage);
        setScrollableItemLastUpdated(targetScrollableIndex);
        setScrollableItem(targetScrollableIndex);
      }

      setLoader(false);
    } catch (err) {
      setLoader(false);
      console.log(err);
      toast.error("Error in getting the next page");
    }
  };

  useEffect(() => {
    if (!projectFile?.id) {
      return;
    }

    const fetchData = async () => {
      try {
        const response = await axios.post(GET_CHARACTERS_FOR_PROJECT, {
          projectId: projectFile.id,
        });

        setCharacters(response.data.characters);
      } catch (err) {
        console.log(err);
      }
    };

    fetchData();
  }, [projectFile?.id]);

  useEffect(() => {
    loadFirstDialoguePage();
  }, [loadFirstDialoguePage]);

  const stopAllOtherAudio = useCallback((currentId) => {
    translatedAudioRefs.current.forEach((player, index) => {
      if (player && index !== currentId) {
        player.pause();
      }
    });

    originalAudioRefs.current.forEach((player, index) => {
      if (player && index !== currentId && player.audioEl.current) {
        player.audioEl.current.pause();
      }
    });
  }, []);

  const findPlayableIndexAtTime = useCallback(
    (timeSec) => {
      if (!Array.isArray(audioData) || audioData.length === 0) return null;

      const hasTranslated = (d) => {
        const tr = findTransObjectsbyLangCode(d.translations);
        return !!tr?.translatedAudio;
      };

      const hasOriginal = (d) => !!d.originalAudio;

      const isPlayable = (d) => {
        if (d.subtitleOnly === true) return false;

        if (d.keepOriginalText) return hasOriginal(d);

        return hasTranslated(d) || hasOriginal(d);
      };

      return findLatestDialogueIndexInTimeRange(
        audioData,
        timeSec,
        isPlayable
      );
    },
    [audioData, findTransObjectsbyLangCode]
  );

  const playRow = useCallback(
    (idx, resumeAt = 0) => {
      if (
        idx === null ||
        idx === undefined ||
        idx < 0 ||
        idx >= audioData.length
      ) {
        return false;
      }

      const row = audioData[idx];
      const tr = findTransObjectsbyLangCode(row.translations);
      const useOriginal = row.keepOriginalText || !tr?.translatedAudio;
      const offsetSec =
        Number.isFinite(resumeAt) && resumeAt > 0 ? resumeAt : 0;

      stopAllOtherAudio(idx);

      if (!useOriginal) {
        const p = translatedAudioRefs.current[idx];

        if (p?.play) {
          p.seek?.(offsetSec);
          p.play();
          setCurrentlyPlayingId(`translated-${idx}`);
          return true;
        }
      } else {
        const el = originalAudioRefs.current[idx]?.audioEl?.current;

        if (el) {
          el.currentTime = offsetSec;
          el.play().catch(() => {});
          setCurrentlyPlayingId(`original-${idx}`);
          return true;
        }
      }

      return false;
    },
    [audioData, findTransObjectsbyLangCode, stopAllOtherAudio]
  );

  const requestSyncPlay = useCallback(() => {
    setPreviewWaitingForVideo(false);
    setPlayPausePlayer("play");

    // Unlock every translated player while this call still has a user gesture.
    translatedAudioRefs.current.forEach((player) => player?.unlock?.());

    if (extendedPreviewDialogueIndex !== null) {
      const extendedPlayer =
        translatedAudioRefs.current[extendedPreviewDialogueIndex];

      extendedPlayer?.play?.();
      setCurrentlyPlayingId(`translated-${extendedPreviewDialogueIndex}`);
      return;
    }

    const timeSec = Number(currentTime) || 0;
    const idx = findPlayableIndexAtTime(timeSec);

    if (
      idx !== null &&
      idx !== undefined &&
      idx !== completedPreviewDialogueIndex
    ) {
      const startSec = Number(audioData[idx]?.start) || 0;
      const resumeAt = Math.max(0, timeSec - startSec);

      if (playRow(idx, resumeAt)) {
        return;
      }
    }
  }, [
    audioData,
    completedPreviewDialogueIndex,
    currentTime,
    findPlayableIndexAtTime,
    playRow,
    extendedPreviewDialogueIndex,
  ]);

  const requestSyncPause = useCallback(() => {
    translatedAudioRefs.current.forEach((p) => p?.pause?.());
    originalAudioRefs.current.forEach((p) => p?.audioEl?.current?.pause?.());
    setPendingPreviewDialogueIndex(null);
    setPreviewWaitingForVideo(false);
    setCurrentlyPlayingId(null);
    setPlayPausePlayer("pause");
  }, []);

  const handlePreviewVideoWaiting = useCallback(() => {
    if (!syncPreview || playPausePlayer !== "play") {
      return;
    }

    translatedAudioRefs.current.forEach((player) => player?.pause?.());
    originalAudioRefs.current.forEach((player) =>
      player?.audioEl?.current?.pause?.()
    );
    setPreviewWaitingForVideo(true);
    setCurrentlyPlayingId(null);
    setPlayPausePlayer("pause");
  }, [playPausePlayer, syncPreview]);

  const handlePreviewVideoReady = useCallback(() => {
    if (!previewWaitingForVideo || !syncPreview) {
      return;
    }

    setPreviewWaitingForVideo(false);
    requestSyncPlay();
  }, [previewWaitingForVideo, requestSyncPlay, syncPreview]);

  // Stop preview at the end of the loaded page so unloaded dialogue audio is never skipped.
  useEffect(() => {
    if (!syncPreview || extendedPreviewDialogueIndex !== null) {
      return;
    }

    const pageEndTime = getDialoguePageEndTime(audioData);
    const videoTime = Number(currentTime);

    if (pageEndTime === null || !Number.isFinite(videoTime)) {
      return;
    }

    if (videoTime < pageEndTime) {
      return;
    }

    if (playPausePlayer === "play") {
      requestSyncPause();

      if (
        continuePreviewAcrossPages &&
        Number(pageNumber) < Number(totalPages)
      ) {
        const targetPage = Number(pageNumber) + 1;

        setPendingContinuousPreviewPage(targetPage);
        toast.info("Loading the next dialogue page…");

        nextPrevPageAPI("next").then((pageLoaded) => {
          if (!pageLoaded) {
            setPendingContinuousPreviewPage(null);
          }
        });
        return;
      }

      toast.info(
        Number(pageNumber) >= Number(totalPages)
          ? "Preview paused at the end of the final dialogue page."
          : "Preview paused at the end of the loaded dialogue page. Move to the next page to continue."
      );
    }
  }, [
    audioData,
    continuePreviewAcrossPages,
    currentTime,
    completedPreviewDialogueIndex,
    extendedPreviewDialogueIndex,
    nextPrevPageAPI,
    pageNumber,
    playPausePlayer,
    requestSyncPause,
    syncPreview,
    totalPages,
  ]);

  // Resume only after the next page's first upcoming audio player is ready.
  useEffect(() => {
    if (
      pendingContinuousPreviewPage === null ||
      !syncPreview ||
      Number(pageNumber) !== Number(pendingContinuousPreviewPage) ||
      pageButtonActivity
    ) {
      return;
    }

    const videoTime = Number(currentTime) || 0;
    const upcomingIndex = audioData.findIndex((dialogue) => {
      if (
        dialogue?.subtitleOnly === true ||
        Number(dialogue?.end) <= videoTime
      ) {
        return false;
      }

      const translation = findTransObjectsbyLangCode(dialogue?.translations);
      return Boolean(
        dialogue?.keepOriginalText
          ? dialogue?.originalAudio
          : translation?.translatedAudio || dialogue?.originalAudio
      );
    });

    if (upcomingIndex === -1) {
      setPendingContinuousPreviewPage(null);
      toast.info("The next page has no playable dialogue audio.");
      return;
    }

    const upcomingDialogue = audioData[upcomingIndex];
    const translation = findTransObjectsbyLangCode(
      upcomingDialogue.translations
    );
    const useOriginal =
      upcomingDialogue.keepOriginalText || !translation?.translatedAudio;
    let attempts = 0;

    const resumeWhenReady = () => {
      const translatedPlayer = translatedAudioRefs.current[upcomingIndex];
      const originalPlayer =
        originalAudioRefs.current[upcomingIndex]?.audioEl?.current;
      const playerReady = useOriginal
        ? Boolean(originalPlayer && originalPlayer.readyState >= 1)
        : Boolean(translatedPlayer?.isLoaded?.());

      if (playerReady) {
        setPendingContinuousPreviewPage(null);
        requestSyncPlay();
        return true;
      }

      attempts += 1;
      if (attempts >= 150) {
        setPendingContinuousPreviewPage(null);
        toast.error(
          "The next page loaded, but its preview audio is not ready. Press Play to retry."
        );
        return true;
      }

      return false;
    };

    let readinessTimer = null;

    if (!resumeWhenReady()) {
      readinessTimer = setInterval(() => {
        if (resumeWhenReady()) {
          clearInterval(readinessTimer);
        }
      }, 100);
    }

    return () => {
      if (readinessTimer) {
        clearInterval(readinessTimer);
      }
    };
  }, [
    audioData,
    currentTime,
    findTransObjectsbyLangCode,
    pageButtonActivity,
    pageNumber,
    pendingContinuousPreviewPage,
    requestSyncPlay,
    syncPreview,
  ]);

  useEffect(() => {
    if (!continuePreviewAcrossPages || !syncPreview) {
      setPendingContinuousPreviewPage(null);
    }
  }, [continuePreviewAcrossPages, syncPreview]);

  useEffect(() => {
    if (!syncPreview) {
      setExtendedPreviewDialogueIndex(null);
      setCompletedPreviewDialogueIndex(null);
      setPendingPreviewDialogueIndex(null);
      setPreviewWaitingForVideo(false);
    }
  }, [syncPreview]);

  // Keep the video paused until the dialogue player at its current position is ready.
  useEffect(() => {
    if (pendingPreviewDialogueIndex === null || !syncPreview) {
      return undefined;
    }

    let attempts = 0;
    const pendingDialogue = audioData[pendingPreviewDialogueIndex];
    const translation = findTransObjectsbyLangCode(
      pendingDialogue?.translations
    );
    const useOriginal =
      pendingDialogue?.keepOriginalText || !translation?.translatedAudio;

    const resumeWhenReady = () => {
      const translatedPlayer =
        translatedAudioRefs.current[pendingPreviewDialogueIndex];
      const originalPlayer =
        originalAudioRefs.current[pendingPreviewDialogueIndex]?.audioEl
          ?.current;
      const playerReady = useOriginal
        ? Boolean(originalPlayer && originalPlayer.readyState >= 1)
        : Boolean(translatedPlayer?.isLoaded?.());

      if (playerReady) {
        const videoTime = Number(currentTime) || 0;
        const dialogueStart = Number(pendingDialogue?.start) || 0;

        setPendingPreviewDialogueIndex(null);
        setPlayPausePlayer("play");
        playRow(
          pendingPreviewDialogueIndex,
          Math.max(0, videoTime - dialogueStart)
        );
        return true;
      }

      attempts += 1;
      if (attempts >= 150) {
        setPendingPreviewDialogueIndex(null);
        toast.error(
          "Preview is paused because the dialogue audio could not be loaded. Press Play to retry."
        );
        return true;
      }

      return false;
    };

    if (resumeWhenReady()) {
      return undefined;
    }

    const readinessTimer = setInterval(() => {
      if (resumeWhenReady()) {
        clearInterval(readinessTimer);
      }
    }, 100);

    return () => clearInterval(readinessTimer);
  }, [
    audioData,
    currentTime,
    findTransObjectsbyLangCode,
    pendingPreviewDialogueIndex,
    playRow,
    syncPreview,
  ]);

  const requestSyncSetSpeed = useCallback((rate) => {
    originalAudioRefs.current.forEach((p) => {
      const el = p?.audioEl?.current;

      if (el) el.playbackRate = rate;
    });

    translatedAudioRefs.current.forEach((p) => {
      if (!p) return;

      if (typeof p.setPlaybackRate === "function") {
        p.setPlaybackRate(rate);
      } else if (p.audioEl?.current) {
        p.audioEl.current.playbackRate = rate;
      }
    });
  }, []);

  const handleAudioEnded = useCallback((dialogueIndex) => {
    if (extendedPreviewDialogueIndex === dialogueIndex) {
      setExtendedPreviewDialogueIndex(null);
    }

    if (syncPreview) {
      setCompletedPreviewDialogueIndex(dialogueIndex);
    }

    setCurrentlyPlayingId(null);
  }, [extendedPreviewDialogueIndex, syncPreview]);

  // Keep the selected dialogue audio locked to the video's authoritative clock.
  useEffect(() => {
    if (!syncPreview || playPausePlayer !== "play") {
      return;
    }

    const videoTimeSeconds = Number(currentTime) || 0;
    const activeIndex = findPlayableIndexAtTime(videoTimeSeconds);

    if (completedPreviewDialogueIndex !== null) {
      if (activeIndex === completedPreviewDialogueIndex) {
        return;
      }

      setCompletedPreviewDialogueIndex(null);
    }

    if (extendedPreviewDialogueIndex !== null) {
      if (
        activeIndex !== null &&
        activeIndex !== extendedPreviewDialogueIndex
      ) {
        const upcomingDialogueStart = Number(audioData[activeIndex]?.start) || 0;

        setExtendedPreviewDialogueIndex(null);
        playRow(
          activeIndex,
          Math.max(0, videoTimeSeconds - upcomingDialogueStart)
        );
        return;
      }

      const extendedDialogue = audioData[extendedPreviewDialogueIndex];
      const extendedPlayer =
        translatedAudioRefs.current[extendedPreviewDialogueIndex];
      const extendedTargetTime = Math.max(
        0,
        videoTimeSeconds - (Number(extendedDialogue?.start) || 0)
      );
      const extendedAudioTime = Number(extendedPlayer?.getCurrentTime?.());

      if (
        !Number.isFinite(extendedAudioTime) ||
        Math.abs(extendedAudioTime - extendedTargetTime) > 0.1
      ) {
        extendedPlayer?.seek?.(extendedTargetTime);
      }

      if (extendedPlayer && !extendedPlayer.isPlaying?.()) {
        extendedPlayer.play?.();
      }

      return;
    }

    if (activeIndex === null) {
      translatedAudioRefs.current.forEach((player) => player?.pause?.());
      originalAudioRefs.current.forEach((player) =>
        player?.audioEl?.current?.pause?.()
      );

      if (currentlyPlayingId !== null) {
        setCurrentlyPlayingId(null);
      }
      return;
    }

    const dialogue = audioData[activeIndex];
    const translation = findTransObjectsbyLangCode(dialogue.translations);
    const useOriginal = dialogue.keepOriginalText || !translation?.translatedAudio;
    const playerKind = useOriginal ? "original" : "translated";
    const desiredPlayingId = `${playerKind}-${activeIndex}`;
    const dialogueStart = Number(dialogue.start) || 0;
    const targetAudioTime = Math.max(0, videoTimeSeconds - dialogueStart);
    const translatedPlayer = translatedAudioRefs.current[activeIndex];
    const originalPlayer = originalAudioRefs.current[activeIndex]?.audioEl?.current;
    const loadedTranslatedAudioDuration = Number(
      translatedPlayer?.getDuration?.()
    );
    const persistedTranslatedAudioDuration = Number(
      translation?.translatedAudioDuration
    );
    const audioDuration = useOriginal
      ? Number(originalPlayer?.duration)
      : loadedTranslatedAudioDuration > 0
      ? loadedTranslatedAudioDuration
      : persistedTranslatedAudioDuration;

    const playerReady = useOriginal
      ? Boolean(originalPlayer && originalPlayer.readyState >= 1)
      : Boolean(translatedPlayer?.isLoaded?.());

    if (!playerReady) {
      translatedAudioRefs.current.forEach((player) => player?.pause?.());
      originalAudioRefs.current.forEach((player) =>
        player?.audioEl?.current?.pause?.()
      );
      setPendingPreviewDialogueIndex(activeIndex);
      setCurrentlyPlayingId(null);
      setPlayPausePlayer("pause");
      return;
    }

    const shouldExtendTranslatedAudio =
      !useOriginal &&
      shouldExtendPreviewTranslatedAudio({
        dialogueStart: dialogue.start,
        dialogueEnd: dialogue.end,
        audioDuration,
        targetAudioTime,
      });

    if (shouldExtendTranslatedAudio) {
      if (currentlyPlayingId !== desiredPlayingId) {
        playRow(activeIndex, targetAudioTime);
      } else if (translatedPlayer && !translatedPlayer.isPlaying?.()) {
        translatedPlayer.seek?.(targetAudioTime);
        translatedPlayer.play?.();
      }

      setExtendedPreviewDialogueIndex(activeIndex);
      return;
    }

    if (
      Number.isFinite(audioDuration) &&
      audioDuration > 0 &&
      targetAudioTime >= audioDuration
    ) {
      if (useOriginal) {
        originalPlayer?.pause?.();
      } else {
        translatedPlayer?.pause?.();
      }
      if (currentlyPlayingId !== desiredPlayingId) {
        setCurrentlyPlayingId(desiredPlayingId);
      }
      return;
    }

    if (currentlyPlayingId !== desiredPlayingId) {
      playRow(activeIndex, targetAudioTime);
      return;
    }

    const audioTime = useOriginal
      ? Number(originalPlayer?.currentTime)
      : Number(translatedPlayer?.getCurrentTime?.());

    if (!Number.isFinite(audioTime) || Math.abs(audioTime - targetAudioTime) > 0.1) {
      if (useOriginal && originalPlayer) {
        originalPlayer.currentTime = targetAudioTime;
      } else {
        translatedPlayer?.seek?.(targetAudioTime);
      }
    }

    if (useOriginal) {
      if (originalPlayer?.paused) {
        originalPlayer.play().catch(() => {});
      }
    } else if (translatedPlayer && !translatedPlayer.isPlaying?.()) {
      translatedPlayer.play?.();
    }
  }, [
    audioData,
    currentTime,
    completedPreviewDialogueIndex,
    currentlyPlayingId,
    findPlayableIndexAtTime,
    findTransObjectsbyLangCode,
    playPausePlayer,
    playRow,
    extendedPreviewDialogueIndex,
    syncPreview,
  ]);

  useEffect(() => {
    const translatedPlayers = [...translatedAudioRefs.current];
    const originalPlayers = [...originalAudioRefs.current];

    return () => {
      translatedPlayers.forEach((player) => {
        if (player) {
          player.pause();
        }
      });

      originalPlayers.forEach((player) => {
        if (player && player.audioEl.current) {
          player.audioEl.current.pause();
        }
      });
    };
  }, []);

  const handleSubtitleOnlyChange = async (dialogueId, isSubtitleOnly) => {
    try {
      const updatedData = [...audioData];
      const dialogueIndex = updatedData.findIndex(
        (item) => item.dbid === dialogueId
      );

      if (dialogueIndex === -1) {
        toast.error("Dialogue not found");
        return;
      }

      updatedData[dialogueIndex] = {
        ...updatedData[dialogueIndex],
        subtitleOnly: isSubtitleOnly,
      };

      setAudioData(updatedData);

      setSubtitleOnlyMap({
        ...subtitleOnlyMap,
        [dialogueId]: isSubtitleOnly,
      });

      const response = await axios.post(UPDATE_ONE_SPEECH, {
        projectId: projectFile.id,
        dialogue: updatedData[dialogueIndex],
      });

      if (response.data) {
        console.log(`Subtitle only status updated for dialogue ${dialogueId}`);
      }
    } catch (err) {
      console.error("Error updating subtitle status:", err);
      toast.error("Error updating subtitle status");
    }
  };

  const handleVEngineTypeChange = async (dialogueId, engineValue) => {
    try {
      const updatedData = [...audioData];
      const dialogueIndex = updatedData.findIndex(
        (item) => item.dbid === dialogueId
      );

      if (dialogueIndex === -1) {
        toast.error("Dialogue not found");
        return;
      }

      updatedData[dialogueIndex] = {
        ...updatedData[dialogueIndex],
        vEngineType: engineValue,
      };

      setAudioData(updatedData);

      setVEngineTypeMap((prev) => ({
        ...prev,
        [dialogueId]: engineValue,
      }));

      const response = await axios.post(UPDATE_ONE_SPEECH, {
        projectId: projectFile.id,
        dialogue: updatedData[dialogueIndex],
      });

      if (response.data) {
        console.log(`Voice engine updated for dialogue ${dialogueId}`);
      }
    } catch (err) {
      console.error("Error updating voice engine:", err);
      toast.error("Error updating voice engine");
    }
  };

  function handleSrtTextChange(e, index) {
    const updatedData = [...audioData];

    updatedData[index].translations = updatedData[index].translations.map(
      (prev) => {
        if (prev.languageCode === languageCode) {
          return {
            ...prev,
            srtText: e.target.value,
          };
        }

        return prev;
      }
    );

    setAudioData(updatedData);
    clearTimeout(timeoutIdRef.current);

    timeoutIdRef.current = setTimeout(async () => {
      await axios.post(UPDATE_ONE_SPEECH, {
        projectId: projectFile.id,
        dialogue: updatedData[index],
      });
    }, 1650);
  }

  const togglePerformanceControls = async (dialogueId, isApproved) => {
    setPerformanceCollapsedMap((prev) => ({
      ...prev,
      [dialogueId]: isApproved,
    }));

    try {
      await axios.post(SET_DIALOGUE_APPROVAL_STATUS, {
        projectId: projectFile.id,
        dialogueId,
        approved: isApproved,
        languageId: languageCode,
      });
    } catch (err) {
      console.error("Error updating approval status:", err);
      toast.error("Error updating approval status");

      setPerformanceCollapsedMap((prev) => ({
        ...prev,
        [dialogueId]: !isApproved,
      }));
    }
  };

  if (!projectFile?.id) {
    return <Loader />;
  }

  return (
    <Grid item container justifyContent="center" marginTop={0.5}>
      <ToastContainer position="top-center" />
      <Loader Load={audioLoader} />

      <Grid item container xs={12} direction="column" sx={{ px: 1 }}>
        <Grid item container direction="row">
          <PerformanceVideoPlayer
            source={videoUrl}
            syncPreview={syncPreview}
            setSyncPreview={setSyncPreview}
            previewVolume={previewVolume}
            onPreviewVolumeChange={setPreviewVolume}
            continuePreviewAcrossPages={continuePreviewAcrossPages}
            setContinuePreviewAcrossPages={setContinuePreviewAcrossPages}
            playPausePlayer={playPausePlayer}
            manualPauseTick={manualPauseTick}
            seekTime={seekTime}
            displayAudioTime={displayAudioTime}
            seekTimeIndex={seekTimeIndex}
            goToPageAccordingTimeAPI={goToPageAccordingTimeAPI}
            onSyncPlay={requestSyncPlay}
            onSyncPause={requestSyncPause}
            onSyncSetSpeed={requestSyncSetSpeed}
            onSyncVideoWaiting={handlePreviewVideoWaiting}
            onSyncVideoReady={handlePreviewVideoReady}
          />

          <Grid
            item
            xs={12}
            sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5, mb: 1 }}
          >
            <DialogueFilterBar
              compact
              value={dialogueSearchFilters}
              onChange={handleDialogueSearch}
              onClear={handleClearDialogueSearch}
              characters={characters}
              getSpeakerDisplayName={findDisplayName}
              emotionOptions={originalAudioEmotionOptions}
              attentionOptions={DIALOGUE_ATTENTION_OPTIONS}
              showNoteFilter
              resultCount={isDialogueSearchActive ? searchResultCount : null}
              totalCount={numberOfDialogues}
              disabled={loader || pageButtonActivity}
            />
          </Grid>

          <Grid item container xs={12}>
            <Grid
              item
              container
              className={classes["AudioWorkPlace"]}
              direction="column"
            >
              <BulkSpeedBar
                selectedCount={selectedOnPageCount}
                run={bulkSpeed.run}
                running={bulkSpeed.running}
                starting={bulkSpeed.starting}
                onApply={handleBulkSpeedApply}
                onClear={() => setSelectedDialogueIds(new Set())}
              />
              <div className={classes["AudioWorkTableWrapper"]}>
                <table className={classes["AudioWorkTable"]}>
                  <thead>
                    <tr className={classes["AudioWorkTableHeadRow"]}>
                      <th
                        className={`${classes["AudioWorkTableHeaderSpacer"]} ${classes["AudioWorkTableColumnCharacter"]}`}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <Checkbox
                            size="small"
                            sx={{ padding: "2px", color: "inherit" }}
                            checked={
                              selectableDialogueIds.length > 0 &&
                              selectedOnPageCount ===
                                selectableDialogueIds.length
                            }
                            indeterminate={
                              selectedOnPageCount > 0 &&
                              selectedOnPageCount <
                                selectableDialogueIds.length
                            }
                            disabled={
                              loader ||
                              bulkSpeed.running ||
                              selectableDialogueIds.length === 0
                            }
                            onChange={togglePageSelection}
                            inputProps={{
                              "aria-label": "Select every line on this page",
                            }}
                          />
                          Character
                        </span>
                        <Button
                          size="small"
                          variant="text"
                          onClick={selectFlaggedDialogues}
                          disabled={
                            loader ||
                            bulkSpeed.running ||
                            selectableDialogueIds.length === 0
                          }
                          title="Select every line on this page tagged Make It Longer or Make It Shorter"
                          sx={{
                            display: "block",
                            color: "inherit",
                            fontSize: "11px",
                            padding: "0 4px",
                            minWidth: 0,
                            textTransform: "none",
                            textDecoration: "underline",
                          }}
                        >
                          Select flagged
                        </Button>
                      </th>
                      <th
                        className={`${classes["AudioWorkTableHeaderCell"]} ${classes["AudioWorkTableColumnOriginal"]}`}
                      >
                        Original
                      </th>
                      <th
                        className={`${classes["AudioWorkTableHeaderCell"]} ${classes["AudioWorkTableColumnTranslation"]}`}
                      >
                        Translation
                      </th>
                      <th
                        className={`${classes["AudioWorkTableHeaderCell"]} ${classes["AudioWorkTableColumnNewAudio"]}`}
                      >
                        Performance Direct
                      </th>
                      <th
                        className={`${classes["AudioWorkTableHeaderCell"]} ${classes["AudioWorkTableColumnSrt"]}`}
                      >
                        SRT
                      </th>
                      <th
                        className={`${classes["AudioWorkTableHeaderCell"]} ${classes["AudioWorkTableColumnNotes"]}`}
                      >
                        Notes &amp; Rating
                      </th>
                    </tr>
                  </thead>

                  <tbody
                    className={classes["AudioWorkPlaceChatBoxScroller"]}
                    ref={containerRef}
                  >
                    {loader ? (
                      <DialogueTableSkeletonRows rowCount={itemsPerPage} />
                    ) : audioData ? (
                      <>
                        {audioData.map((data, index) => {
                          const translation = findTransObjectsbyLangCode(
                            data?.translations
                          );

                          const translationDuration =
                            translation?.translatedAudioDuration ??
                            data?.translatedAudioDurationTemp;

                          const durationNote = checkTranslationLength(
                            data.end - data.start,
                            translationDuration
                          );

                          const recommendedSpeedOption =
                            getRecommendedSpeedOption(
                              data.end - data.start,
                              translationDuration
                            );

                          const overlapDuration = translation?.translatedAudio
                            ? translation?.translatedAudioDuration
                            : null;

                          const translationText =
                            translation?.translatedText || "";

                          const srtText = translation?.srtText;

                          const translationNote =
                            translation?.translatedAudioNote ?? "";

                          const translationRating =
                            translation?.translatedAudioRating ?? 0;

                          const srtDisplayText =
                            srtText === undefined ||
                            srtText === null ||
                            srtText === ""
                              ? translationText
                              : srtText;

                          const isPerformanceCollapsed =
                            performanceCollapsedMap[data.dbid] ?? false;

                          const storedVoiceEngine =
                            vEngineTypeMap[data.dbid] || DEFAULT_VOICE_ENGINE;

                          const selectedVoiceEngine =
                            getSupportedVoiceEngine(storedVoiceEngine);

                          const isLegacyEngine =
                            isLegacyVoiceEngine(storedVoiceEngine);

                          const hasUnsupportedVoiceEngine =
                            !isSupportedVoiceEngine(storedVoiceEngine);

                          const isSoundEffectEngine =
                            selectedVoiceEngine === SOUND_EFFECT_VOICE_ENGINE;

                          const isBulkSelectable = isBulkSpeedSelectable(
                            data,
                            languageCode,
                            subtitleOnlyMap
                          );
                          const isBulkSpeedPending = bulkSpeedPendingIds.has(
                            data.dbid
                          );

                          return (
                            <tr
                              key={data.dbid || index}
                              className={classes["AudioWorkTableRow"]}
                            >
                              <td
                                className={`${classes["AudioWorkMetaColumn"]} ${classes["AudioWorkTableColumnCharacter"]}`}
                              >
                                <Grid
                                  container
                                  direction="column"
                                  gap={1.5}
                                  alignItems="stretch"
                                >
                                  <Grid
                                    item
                                    container
                                    alignItems="center"
                                    justifyContent="center"
                                    gap={0.5}
                                  >
                                    <Checkbox
                                      size="small"
                                      sx={{ padding: "2px" }}
                                      checked={selectedDialogueIds.has(
                                        data.dbid
                                      )}
                                      disabled={
                                        !isBulkSelectable || bulkSpeed.running
                                      }
                                      onChange={() =>
                                        toggleDialogueSelection(data.dbid)
                                      }
                                      inputProps={{
                                        "aria-label": `Select line ${
                                          (pageNumber - 1) * itemsPerPage +
                                          index +
                                          1
                                        }`,
                                      }}
                                    />
                                    {isBulkSpeedPending ? (
                                      <span
                                        title="Adjusting speed"
                                        style={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: "4px",
                                          fontSize: "12px",
                                        }}
                                      >
                                        <ClipLoader size={12} />
                                        Adjusting
                                      </span>
                                    ) : null}
                                  </Grid>
                                  <Grid
                                    item
                                    container
                                    justifyContent="center"
                                    height="25px"
                                  >
                                    {data.speaker ? (
                                      <Grid>
                                        <Select
                                          value={data.speaker}
                                          onChange={(e) =>
                                            handleSpeakerChange(e, data.id)
                                          }
                                          input={<BootstrapInputForAvalialbe />}
                                        >
                                          {characters?.map(
                                            (char, charIndex) => (
                                              <MenuItem
                                                key={charIndex}
                                                value={char.speaker}
                                                className={
                                                  classes[
                                                    "DialogueRowCharacterOption"
                                                  ]
                                                }
                                              >
                                                <span>
                                                  {findDisplayName(
                                                    char.speaker
                                                  )}
                                                </span>

                                                {!!char.projectId && (
                                                  <span
                                                    title="Imported Character"
                                                    className={
                                                      classes[
                                                        "DialogueRowImportedCharacterIndicator"
                                                      ]
                                                    }
                                                  >
                                                    ↙️
                                                  </span>
                                                )}
                                              </MenuItem>
                                            )
                                          )}
                                        </Select>
                                      </Grid>
                                    ) : (
                                      <Grid marginBottom={1}>
                                        <Select
                                          value={data.speaker}
                                          onChange={(e) =>
                                            handleSpeakerChange(e, data.id)
                                          }
                                          input={<BootstrapInput />}
                                        >
                                          {characters?.map((char) => (
                                            <MenuItem
                                              key={char.speaker}
                                              value={char.speaker}
                                              className={
                                                classes[
                                                  "DialogueRowCharacterSimpleOption"
                                                ]
                                              }
                                            >
                                              {findDisplayName(char.speaker)}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </Grid>
                                    )}
                                  </Grid>

                                  <Grid item>
                                    <InputMask
                                      mask="99:99:99.99"
                                      value={inputStartTime[index] || ""}
                                      onChange={(e) =>
                                        handleStartTimeChange(e, index)
                                      }
                                      className={`${classes["AudioWorkPlaceTimelineEditTranslation"]} ${classes["DialogueRowTimelineField"]}`}
                                      placeholder="HH:MM:SS.MS"
                                    >
                                      {(inputProps) => (
                                        <InputBase
                                          {...inputProps}
                                          fullWidth
                                          className={
                                            classes["DialogueRowTimelineInput"]
                                          }
                                        />
                                      )}
                                    </InputMask>
                                  </Grid>

                                  <Grid item>
                                    <InputMask
                                      mask="99:99:99.99"
                                      value={inputEndTime[index] || ""}
                                      onChange={(e) =>
                                        handleEndTimeChange(e, index)
                                      }
                                      className={`${classes["AudioWorkPlaceTimelineEditTranslation"]} ${classes["DialogueRowTimelineField"]}`}
                                      placeholder="HH:MM:SS.MS"
                                    >
                                      {(inputProps) => (
                                        <InputBase
                                          {...inputProps}
                                          fullWidth
                                          className={
                                            classes["DialogueRowTimelineInput"]
                                          }
                                        />
                                      )}
                                    </InputMask>
                                  </Grid>
                                </Grid>
                              </td>

                              <td
                                className={`${classes["AudioWorkDataColumn"]} ${classes["AudioWorkTableColumnOriginal"]}`}
                              >
                                <Grid container direction="column">
                                  <Grid item>
                                    <Grid
                                      item
                                      className={
                                        classes["DialogueRowHeaderLine"]
                                      }
                                    >
                                      <FormControlLabel
                                        className={
                                          classes["DialogueRowSwitchControl"]
                                        }
                                        control={
                                          <RowFlagSwitch
                                            checked={!!data.keepOriginalText}
                                            onChange={() =>
                                              handleKeepOriginalText(data.id)
                                            }
                                          />
                                        }
                                        label="Use original"
                                      />
                                      <NonverbalLineNotice
                                        dialogue={data}
                                        projectId={projectFile.id}
                                        onReviewed={(changes) =>
                                          setAudioData((current) =>
                                            current.map((dia) =>
                                              dia.dbid === data.dbid ? { ...dia, ...changes } : dia
                                            )
                                          )
                                        }
                                      />
                                    </Grid>

                                    <div
                                      className={
                                        classes["DialogueRowAudioSection"]
                                      }
                                      style={{ width: "98%" }}
                                    >
                                      <NativeClipAudioPlayer
                                        ref={(el) =>
                                          (originalAudioRefs.current[index] = el)
                                        }
                                        src={data.originalAudio}
                                        timeDecimalPlaces={3}
                                        // HTML audio cannot exceed 1, so boosts only apply to translated clips.
                                        volume={
                                          syncPreview
                                            ? Math.min(previewVolume, 1)
                                            : 1
                                        }
                                        onPlay={() => {
                                          stopAllOtherAudio(index);
                                          setCurrentlyPlayingId(
                                            `original-${index}`
                                          );
                                        }}
                                        onPause={() => {
                                          setCurrentlyPlayingId((playingId) =>
                                            playingId === `original-${index}`
                                              ? null
                                              : playingId
                                          );
                                        }}
                                        onEnded={() => handleAudioEnded(index)}
                                      />
                                    </div>
                                  </Grid>

                                  <Grid item>
                                    <textarea
                                      className={`${
                                        classes["AudioWorkTextEditInput"]
                                      } ${classes["DialogueRowJoinedTextArea"]} ${
                                        currentTime >= data.start &&
                                        currentTime < data.end
                                          ? classes["DialogueRowActiveText"]
                                          : ""
                                      }`}
                                      value={data.text}
                                      type="text"
                                      onFocus={() =>
                                        handleTextFocus(index, data.start)
                                      }
                                      onChange={(e) =>
                                        handleTextChange(e, data.id)
                                      }
                                    ></textarea>
                                  </Grid>
                                </Grid>
                              </td>

                              <td
                                className={`${classes["AudioWorkDataColumn"]} ${classes["AudioWorkTableColumnTranslation"]}`}
                              >
                                <Grid container direction="row">
                                  {data.keepOriginalText ? (
                                    <></>
                                  ) : (
                                    <>
                                      <Grid item container direction="column">
                                        <Grid
                                          item
                                          fontSize="var(--font-size-normal)"
                                          color="green"
                                          className={`${
                                            classes["DialogueRowHeaderLine"]
                                          } ${
                                            classes["DialogueRowStatusTags"]
                                          }`}
                                        >
                                          {durationNote ? (
                                            <span
                                              className={`${
                                                classes["DialogueRowStatusTag"]
                                              } ${
                                                durationNote.label === "Perfect"
                                                  ? classes[
                                                      "DialogueRowStatusTagSuccess"
                                                    ]
                                                  : ""
                                              }`}
                                            >
                                              {durationNote.label}
                                            </span>
                                          ) : null}
                                          {handleVoiceOverLapping(
                                            data.id,
                                            overlapDuration
                                          )}
                                        </Grid>

                                        <Grid
                                          item
                                          container
                                          direction="row"
                                          alignItems="center"
                                          width="98%"
                                          className={`${
                                            classes[
                                              "DialogueRowTranslatedPlayerRow"
                                            ]
                                          } ${
                                            classes["DialogueRowAudioSection"]
                                          }`}
                                        >
                                          <Grid item height="100%">
                                            <VolumeSelector
                                              className={
                                                classes[
                                                  "DialogueRowCompactVolume"
                                                ]
                                              }
                                              value={
                                                translation?.translatedAudioVolume ??
                                                null
                                              }
                                              onChange={(e) => {
                                                handleTranslatedAudioVolume(
                                                  e,
                                                  data.id
                                                );
                                              }}
                                            />
                                          </Grid>

                                          <Grid item xs height="100%">
                                            <ClipAudioPlayer
                                              compact
                                              embedded
                                              ref={(el) =>
                                                (translatedAudioRefs.current[
                                                  index
                                                ] = el)
                                              }
                                              src={translation?.translatedAudio}
                                              autoPlayRequest={
                                                audioAutoPlayRequestMap[
                                                  data.dbid
                                                ]
                                              }
                                              decible={
                                                translation?.translatedAudioVolume ??
                                                null
                                              }
                                              playbackVolume={
                                                syncPreview ? previewVolume : 1
                                              }
                                              showScrubBar={false}
                                              timeDecimalPlaces={3}
                                              onPlay={() => {
                                                stopAllOtherAudio(index);
                                                setCurrentlyPlayingId(
                                                  `translated-${index}`
                                                );
                                              }}
                                              onEnd={() =>
                                                handleAudioEnded(index)
                                              }
                                              onPause={() => {
                                                setCurrentlyPlayingId(
                                                  (playingId) =>
                                                    playingId ===
                                                    `translated-${index}`
                                                      ? null
                                                      : playingId
                                                );
                                              }}
                                            />
                                          </Grid>
                                        </Grid>

                                        <Grid item>
                                          <textarea
                                            className={`${
                                              classes["AudioWorkTextEditInput"]
                                            } ${
                                              classes["DialogueRowJoinedTextArea"]
                                            } ${
                                              currentTime >= data.start &&
                                              currentTime < data.end
                                                ? classes[
                                                    "DialogueRowActiveText"
                                                  ]
                                                : ""
                                            }`}
                                            value={translationText}
                                            type="text"
                                            onFocus={() =>
                                              handleTextFocus(index, data.start)
                                            }
                                            onChange={(e) =>
                                              handleTranslationChange(
                                                e,
                                                data.id
                                              )
                                            }
                                          ></textarea>
                                        </Grid>
                                      </Grid>
                                    </>
                                  )}
                                </Grid>
                              </td>

                              <td
                                className={`${classes["AudioWorkDataColumn"]} ${classes["AudioWorkTableColumnNewAudio"]}`}
                              >
                                <Grid container direction="column">
                                  {isPerformanceCollapsed ? (
                                    <Grid
                                      item
                                      container
                                      justifyContent="center"
                                    >
                                      <TaskButton
                                        onClick={() =>
                                          togglePerformanceControls(
                                            data.dbid,
                                            false
                                          )
                                        }
                                        width={performanceToggleButtonWidth}
                                      >
                                        Reopen
                                      </TaskButton>
                                    </Grid>
                                  ) : (
                                    <>
                                      <Grid
                                        item
                                        className={
                                          classes["DialogueRowHeaderLine"]
                                        }
                                      >
                                        <FormControlLabel
                                          className={
                                            classes["DialogueRowSwitchControl"]
                                          }
                                          control={
                                            <RowFlagSwitch
                                              checked={
                                                subtitleOnlyMap[data.dbid] ===
                                                true
                                              }
                                              onChange={(e) =>
                                                handleSubtitleOnlyChange(
                                                  data.dbid,
                                                  e.target.checked
                                                )
                                              }
                                            />
                                          }
                                          label="Subtitle only"
                                        />
                                      </Grid>

                                      <Grid
                                        item
                                        container
                                        justifyContent="flex-start"
                                        alignItems="center"
                                        alignContent="center"
                                        direction="row"
                                        gap={0.6}
                                      >
                                        {!isSoundEffectEngine && (
                                          <Grid
                                            item
                                            fontSize="var(--font-size-normal)"
                                            className={
                                              classes[
                                                "DialogueRowEmotionSelectorWrapper"
                                              ]
                                            }
                                          >
                                            {(() => {
                                              // One selector per line, even if the project repeats a key.
                                              const char = getLineCharacter(
                                                characters,
                                                data.speaker
                                              );
                                              return char ? (
                                                    <select
                                                      value={
                                                        data.tempTransEmotion
                                                      }
                                                      onChange={(e) => {
                                                        const updatedData = [
                                                          ...audioData,
                                                        ];

                                                        updatedData[data.id] = {
                                                          ...updatedData[
                                                            data.id
                                                          ],
                                                          tempTransEmotion:
                                                            e.target.value,
                                                        };

                                                        setAudioData(
                                                          updatedData
                                                        );
                                                      }}
                                                      disabled={
                                                        data.keepOriginalText ||
                                                        subtitleOnlyMap[
                                                          data.dbid
                                                        ] === true
                                                      }
                                                      className={`${
                                                        classes[
                                                          "AudioWorkSelector"
                                                        ]
                                                      } ${
                                                        data.keepOriginalText ||
                                                        subtitleOnlyMap[
                                                          data.dbid
                                                        ] === true
                                                          ? classes[
                                                              "DialogueRowSelectDisabled"
                                                            ]
                                                          : ""
                                                      }`}
                                                    >
                                                      <option value="">
                                                        select emotion
                                                      </option>

                                                      {(() => {
                                                        const options = (
                                                          char.voices ?? []
                                                        ).map((voice) => ({
                                                          label: voice.emotion,
                                                          value: voice.voice_id,
                                                        }));

                                                        return options.map(
                                                          (opt, optIndex) => (
                                                            <option
                                                              value={opt.value}
                                                              key={optIndex}
                                                            >
                                                              {opt.label}
                                                            </option>
                                                          )
                                                        );
                                                      })()}
                                                    </select>
                                              ) : null;
                                            })()}
                                          </Grid>
                                        )}

                                        <Grid item xs={1.5}>
                                          <Grid
                                            item
                                            className={`${
                                              classes["DialogueRowActionIcon"]
                                            } ${
                                              data.keepOriginalText ||
                                              subtitleOnlyMap[data.dbid] ===
                                                true
                                                ? classes[
                                                    "DialogueRowActionIconDisabled"
                                                  ]
                                                : ""
                                            }`}
                                          >
                                            {data.loader &&
                                            data.loaderAction === "single" ? (
                                              <ClipLoader
                                                color={secondaryColor}
                                                loading
                                                size={20}
                                              />
                                            ) : (
                                            <Badge badgeContent={1}>
                                              <AddCircleIcon
                                                disabled={
                                                  audioLoader ||
                                                  data.keepOriginalText ||
                                                  subtitleOnlyMap[data.dbid] ===
                                                    true
                                                }
                                                onClick={() => {
                                                  if (
                                                    data.keepOriginalText ||
                                                    subtitleOnlyMap[
                                                      data.dbid
                                                    ] === true
                                                  ) {
                                                    return;
                                                  }

                                                  if (
                                                    !isSoundEffectEngine &&
                                                    audioData[data.id]
                                                      .tempTransEmotion === ""
                                                  ) {
                                                    toast.error(
                                                      "Error !! Choose an emotion"
                                                    );
                                                    return;
                                                  }

                                                  translatedAudioRefs.current[
                                                    index
                                                  ]?.unlock?.();

                                                  setAudioData((prevData) => {
                                                    const updatedData = [
                                                      ...prevData,
                                                    ];
                                                    updatedData[
                                                      data.id
                                                    ].loader = true;
                                                    updatedData[
                                                      data.id
                                                    ].loaderAction = "single";
                                                    return updatedData;
                                                  });

                                                  handleNewAudioUrl(data.id);
                                                }}
                                                className={
                                                  classes["DialogueRowAddIcon"]
                                                }
                                              />
                                            </Badge>
                                            )}
                                          </Grid>
                                        </Grid>

                                        {!isSoundEffectEngine && (
                                          <Grid item xs={1.5}>
                                            <Grid
                                              item
                                              className={`${
                                                classes["DialogueRowActionIcon"]
                                              } ${
                                                data.keepOriginalText ||
                                                subtitleOnlyMap[data.dbid] ===
                                                  true
                                                  ? classes[
                                                      "DialogueRowActionIconDisabled"
                                                    ]
                                                  : ""
                                              }`}
                                            >
                                              {data.loader &&
                                              data.loaderAction ===
                                                "multiple" ? (
                                                <ClipLoader
                                                  color={secondaryColor}
                                                  loading
                                                  size={20}
                                                />
                                              ) : (
                                              <Badge badgeContent={3}>
                                                <AddCircleIcon
                                                  disabled={
                                                    audioLoader ||
                                                    data.keepOriginalText ||
                                                    subtitleOnlyMap[
                                                      data.dbid
                                                    ] === true
                                                  }
                                                  onClick={() => {
                                                    if (
                                                      data.keepOriginalText ||
                                                      subtitleOnlyMap[
                                                        data.dbid
                                                      ] === true
                                                    ) {
                                                      return;
                                                    }

                                                    if (
                                                      audioData[data.id]
                                                        .tempTransEmotion === ""
                                                    ) {
                                                      toast.error(
                                                        "Error !! Choose an emotion"
                                                      );
                                                      return;
                                                    }

                                                    setAudioData((prevData) => {
                                                      const updatedData = [
                                                        ...prevData,
                                                      ];
                                                      updatedData[
                                                        data.id
                                                      ].loader = true;
                                                      updatedData[
                                                        data.id
                                                      ].loaderAction =
                                                        "multiple";
                                                      return updatedData;
                                                    });

                                                    handleMultipleAudio(
                                                      data.id
                                                    );
                                                  }}
                                                  className={
                                                    classes[
                                                      "DialogueRowAddIcon"
                                                    ]
                                                  }
                                                />
                                              </Badge>
                                              )}
                                            </Grid>
                                          </Grid>
                                        )}
                                      </Grid>

                                      <Grid
                                        item
                                        container
                                        marginTop={0.6}
                                        justifyContent="flex-start"
                                        alignItems="center"
                                        alignContent="center"
                                        direction="row"
                                        gap={0.6}
                                      >
                                        <Grid
                                          item
                                          fontSize="var(--font-size-normal)"
                                        >
                                          <React.Fragment key={index}>
                                            <select
                                              className={`${
                                                classes["AudioWorkSelector"]
                                              } ${
                                                data.keepOriginalText ||
                                                subtitleOnlyMap[data.dbid] ===
                                                  true
                                                  ? classes[
                                                      "DialogueRowSelectDisabled"
                                                    ]
                                                  : ""
                                              }`}
                                              value={
                                                getSelectedGeneratedAudioValue(
                                                  translation
                                                )
                                              }
                                              onChange={(e) => {
                                                selectAudioOption(
                                                  e,
                                                  data.id,
                                                  index
                                                );
                                              }}
                                              disabled={
                                                data.keepOriginalText ||
                                                subtitleOnlyMap[data.dbid] ===
                                                  true
                                              }
                                            >
                                              <option value="">
                                                select options
                                              </option>

                                              {data.tempAudioList.map(
                                                (audio, audioIndex) => {
                                                  const emotionLabel =
                                                    audio.emotion ===
                                                      undefined ||
                                                    audio.emotion === null ||
                                                    audio.emotion === ""
                                                      ? ""
                                                      : `${audio.emotion} `;

                                                  return (
                                                    <option
                                                      value={getGeneratedAudioOptionValue(
                                                        audio
                                                      )}
                                                      key={audioIndex}
                                                    >
                                                      {`${audioIndex} - ${emotionLabel}(${audio.length})`}
                                                    </option>
                                                  );
                                                }
                                              )}
                                            </select>
                                          </React.Fragment>
                                        </Grid>

                                        <Grid item>
                                          <Select
                                            size="small"
                                            value={
                                              speedSelectionMap[data.dbid] ?? ""
                                            }
                                            displayEmpty
                                            onChange={(e) => {
                                              const newSpeed = e.target.value;

                                              setSpeedSelectionMap((prev) => ({
                                                ...prev,
                                                [data.dbid]: newSpeed,
                                              }));

                                              handleAdjustSpeed(data, newSpeed);
                                            }}
                                            disabled={
                                              data.keepOriginalText ||
                                              subtitleOnlyMap[data.dbid] ===
                                                true ||
                                              !translation?.translatedAudio ||
                                              isBulkSpeedPending
                                            }
                                            sx={{
                                              // Matches the Close button below it.
                                              width:
                                                performanceToggleButtonWidth,
                                              height: "30px",
                                              fontSize: "14px",
                                              // Tighten MUI's default arrow gutter
                                              // so labels like "Faster 20%" fit.
                                              "&& .MuiSelect-select": {
                                                paddingLeft: "8px",
                                                paddingRight: "20px",
                                              },
                                              "& .MuiSelect-icon": {
                                                right: 0,
                                              },
                                            }}
                                          >
                                            <MenuItem value="" disabled>
                                              Speed
                                            </MenuItem>
                                            <MenuItem value="auto">
                                              Auto
                                            </MenuItem>
                                            <MenuItem
                                              value={1.0}
                                              sx={{
                                                fontWeight:
                                                  recommendedSpeedOption === 1.0
                                                    ? 700
                                                    : 400,
                                              }}
                                            >
                                              Original
                                            </MenuItem>
                                            <MenuItem
                                              value={0.8}
                                              sx={{
                                                fontWeight:
                                                  recommendedSpeedOption === 0.8
                                                    ? 700
                                                    : 400,
                                              }}
                                            >
                                              Slower 20%
                                            </MenuItem>
                                            <MenuItem
                                              value={0.9}
                                              sx={{
                                                fontWeight:
                                                  recommendedSpeedOption === 0.9
                                                    ? 700
                                                    : 400,
                                              }}
                                            >
                                              Slower 10%
                                            </MenuItem>
                                            <MenuItem
                                              value={1.1}
                                              sx={{
                                                fontWeight:
                                                  recommendedSpeedOption === 1.1
                                                    ? 700
                                                    : 400,
                                              }}
                                            >
                                              Faster 10%
                                            </MenuItem>
                                            <MenuItem
                                              value={1.2}
                                              sx={{
                                                fontWeight:
                                                  recommendedSpeedOption === 1.2
                                                    ? 700
                                                    : 400,
                                              }}
                                            >
                                              Faster 20%
                                            </MenuItem>
                                          </Select>
                                        </Grid>

                                        <Grid item>
                                          <FormControl
                                            size="small"
                                            className={
                                              classes[
                                                "DialogueRowVoiceEngineControl"
                                              ]
                                            }
                                          >
                                            <InputLabel
                                              id={`voice-engine-label-${data.id}`}
                                            >
                                              Voice Engine
                                            </InputLabel>

                                            <Select
                                              labelId={`voice-engine-label-${data.id}`}
                                              value={
                                                hasUnsupportedVoiceEngine
                                                  ? storedVoiceEngine
                                                  : selectedVoiceEngine
                                              }
                                              label="Voice Engine"
                                              onChange={(e) =>
                                                handleVEngineTypeChange(
                                                  data.dbid,
                                                  e.target.value
                                                )
                                              }
                                            >
                                              {hasUnsupportedVoiceEngine && (
                                                <MenuItem
                                                  value={storedVoiceEngine}
                                                  disabled
                                                >
                                                  {LEGACY_AUDIO_ENGINE_LABEL}
                                                </MenuItem>
                                              )}
                                              {VOICE_ENGINE_OPTIONS.map(
                                                (option) => (
                                                  <MenuItem
                                                    key={option.value}
                                                    value={option.value}
                                                  >
                                                    {option.label}
                                                  </MenuItem>
                                                )
                                              )}
                                            </Select>
                                          </FormControl>
                                          {hasUnsupportedVoiceEngine && (
                                            <div
                                              style={{
                                                color:
                                                  "var(--color-error, #d32f2f)",
                                                fontSize:
                                                  "var(--font-size-small)",
                                                marginTop: 4,
                                              }}
                                            >
                                              {isLegacyEngine
                                                ? `${LEGACY_AUDIO_ENGINE_LABEL} audio remains playable, but generation with this legacy engine is blocked. Select a supported engine to create new audio.`
                                                : `Unsupported legacy engine "${storedVoiceEngine}" will use the default engine when generating.`}
                                            </div>
                                          )}
                                        </Grid>

                                        <Grid item>
                                          <TaskButton
                                            onClick={() =>
                                              togglePerformanceControls(
                                                data.dbid,
                                                true
                                              )
                                            }
                                            width={performanceToggleButtonWidth}
                                            height="30px"
                                            style={{ fontSize: "14px" }}
                                            disabled={
                                              !translation?.translatedAudio
                                            }
                                          >
                                            Close
                                          </TaskButton>
                                        </Grid>
                                      </Grid>
                                    </>
                                  )}
                                </Grid>
                              </td>

                              <td className={classes["AudioWorkDataColumn"]}>
                                <Grid
                                  container
                                  direction="column"
                                  alignItems="stretch"
                                >
                                  {data.keepOriginalText ? (
                                    <></>
                                  ) : (
                                    <Grid
                                      item
                                      className={
                                        classes["DialogueRowTextBoxOffset"]
                                      }
                                    >
                                      <textarea
                                        className={`${
                                          classes["AudioWorkTextEditInput"]
                                        } ${
                                          currentTime >= data.start &&
                                          currentTime < data.end
                                            ? classes["DialogueRowActiveText"]
                                            : ""
                                        } ${
                                          classes["DialogueRowSrtTextInput"]
                                        }`}
                                        value={srtDisplayText}
                                        rows={5}
                                        type="text"
                                        placeholder="SRT subtitle text"
                                        onChange={(e) =>
                                          handleSrtTextChange(e, data.id)
                                        }
                                      ></textarea>
                                    </Grid>
                                  )}
                                </Grid>
                              </td>

                              <td className={classes["AudioWorkDataColumn"]}>
                                <Grid
                                  container
                                  direction="column"
                                  justifyContent="flex-start"
                                  alignItems="stretch"
                                >
                                  <Grid
                                    item
                                    className={
                                      classes["DialogueRowNotesRatingRow"]
                                    }
                                  >
                                    <Rating
                                      size="small"
                                      className={classes["DialogueRowRating"]}
                                      value={translationRating}
                                      precision={1}
                                      onChange={(event, newValue) => {
                                        handleDialogueRating(newValue, data.id);
                                      }}
                                    />
                                  </Grid>

                                  <Grid item width="100%">
                                    <textarea
                                      className={
                                        classes["AudioWorkNoteEditInput"]
                                      }
                                      value={translationNote}
                                      placeholder="add note here"
                                      type="text"
                                      onChange={(e) =>
                                        handleNoteChange(e, data.id)
                                      }
                                    ></textarea>
                                  </Grid>
                                </Grid>
                              </td>
                            </tr>
                          );
                        })}
                      </>
                    ) : (
                      <></>
                    )}
                  </tbody>
                </table>
              </div>
            </Grid>

            <Grid
              item
              container
              alignItems="center"
              justifyContent="center"
              marginTop={2}
            >
              <Grid
                item
                container
                alignItems="center"
                justifyContent="center"
                gap={2}
              >
                <Grid item>
                  <TaskButton
                    onClick={() => nextPrevPageAPI("previous")}
                    width="80px"
                    disabled={Number(pageNumber) === 1 || pageButtonActivity}
                  >
                    Previous
                  </TaskButton>
                </Grid>

                <Grid item>
                  <span>
                    Page {pageNumber} of {totalPages}
                  </span>
                </Grid>

                <Grid item>
                  <TaskButton
                    width="80px"
                    onClick={() => nextPrevPageAPI("next")}
                    disabled={
                      Number(pageNumber) === totalPages || pageButtonActivity
                    }
                  >
                    Next
                  </TaskButton>
                </Grid>
              </Grid>
            </Grid>

            <Grid
              item
              container
              alignItems="center"
              justifyContent="center"
              gap={4}
              marginTop={1}
              marginLeft={15.5}
            >
              <Grid item>
                <input
                  type="text"
                  style={{ width: "50px", textAlign: "center" }}
                  value={goToPageNumber}
                  onChange={(e) => {
                    setGoToPageNumber(e.target.value >= 1 ? e.target.value : 1);
                  }}
                />
              </Grid>

              <Grid item>
                <TaskButton
                  onClick={() => goToPageAPI(goToPageNumber)}
                  width="80px"
                >
                  Go
                </TaskButton>
              </Grid>
            </Grid>
          </Grid>

          <Grid
            item
            container
            xs={12}
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            marginTop={2}
            gap={3}
          >
            <Grid item xs="auto">
              <input
                style={{ width: "150px", height: "20px" }}
                className={classes["AudioWorkNoteEditInput"]}
                type="text"
                placeholder="Enter version name"
                value={versionName}
                onChange={(e) => setVersionName(formatInputs(e.target.value))}
              />
            </Grid>

            <Grid item sx={{ width: 320 }}>
              <ExportOptions>
                <ExportVideoSourceSelect
                  id="submit-all-video"
                  value={videoSource}
                  onChange={setVideoSource}
                  hasSourceVideo={hasSourceVideo}
                />
                <ExportLayoutSelect
                  id="submit-all-layout"
                  value={outputAudioLayout}
                  onChange={setOutputAudioLayout}
                />
              </ExportOptions>
            </Grid>

            <Grid item>
              <TaskButton onClick={() => finalSubmission()}>
                Submit All
              </TaskButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default VideoPerformanceDirection;
